import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";

import type { DAGData } from "../../api/artifacts";
import {
  canonicalGroundedSourceDigest,
  type GroundedHumanSourceRequest,
  type GroundedImpactSourceRequest,
  type GroundedProofTraceSourceRequest,
  type GroundedTemporalCompareSourceRequest,
} from "../../api/groundedProvenance";
import type { HumanProvenanceRequest } from "../../api/humanProvenance";
import {
  IMPACT_ANALYSIS_DEFAULT_LIMITS,
  type ImpactAnalysisRequest,
} from "../../api/impactAnalysis";
import type {
  CanonicalActorRecord,
  CanonicalArtifactRecord,
  CanonicalArtifactVersionRecord,
  CanonicalBindingRecord,
  CanonicalBindingStateEventRecord,
  CanonicalDecisionRecord,
  CanonicalDecisionEvidenceRecord,
  CanonicalDecisionStatusEventRecord,
  CanonicalJournalEntryRecord,
  CanonicalLifecycleEventRecord,
  CanonicalMergeInputRecord,
  CanonicalOperationRunRecord,
  CanonicalOperationSpecRecord,
  CanonicalOperationStateEventRecord,
  CanonicalSupersessionRecord,
  CanonicalToolSnapshotRecord,
} from "../../api/canonicalLineage";
import { PROOF_TRACE_DEFAULT_LIMITS, type ProofTraceRequest } from "../../api/proofTrace";
import type { LineageDsmArtifactVersionRef } from "../../api/lineageDsm";
import {
  TEMPORAL_COMPARE_DEFAULT_LIMITS,
  type TemporalCompareRequest,
} from "../../api/temporalCompare";
import { normalizeDagData, projectDagGraphByMask } from "./model";
import { resolveDagCategories } from "./layout";
import { DagLayoutWorkerClient } from "./layoutClient";
import { DagCanvasRenderer } from "./renderer";
import {
  AccessibleRecordBrowser,
  ImpactResultsPanel,
  SummaryGroupPanel,
} from "./DagAnalysisPanels";
import { HumanFacetFilters, HumanRecordInspector } from "./HumanProvenancePanel";
import { JournalEntryForm } from "./JournalEntryForm";
import { AuditView, RunMatrixView } from "./DagTableViews";
import { LineageDsmMatrix } from "./LineageDsmMatrix";
import { DAG_VIEW_DEFINITIONS, DagContextBar, DagViewSwitcher } from "./DagViewControls";
import {
  ALL_DAG_PATH_STATES,
  ALL_DAG_RECORD_TYPES,
  ALL_MODEL_CLASSIFICATIONS,
  buildDagFilterMask,
  buildDagSearchDocuments,
  DEFAULT_DAG_FILTERS,
  findDagMatches,
  indicesToMask,
  type DagFilterState,
  type DagTimeWindowDays,
  type DagTopologyFacet,
} from "./search";
import { generateSyntheticDag } from "./syntheticGraph";
import type {
  DagCanvasPreferences,
  DagCategoryDefinition,
  DagCategoryImportance,
  DagFrameBenchmark,
  DagHoverTarget,
  DagViewerGraph,
  DagViewerNode,
} from "./types";
import {
  buildCompareProjection,
  buildFocusProjection,
  buildSummaryProjection,
  type DagCompareProjection,
  type DagFocusProjection,
  type DagSummaryGroup,
  type DagSummaryProjection,
  type DagViewMode,
} from "./viewProjections";
import { parseDagWorkspaceState, pushDagWorkspaceUrl, updateDagWorkspaceUrl } from "./viewState";
import { useCanonicalLineageProjection } from "./useCanonicalLineageProjection";
import { useLineageDsm } from "./useLineageDsm";
import { parseCanonicalLineageRefKey } from "./canonicalAdapter";
import {
  composeHumanProvenanceDag,
  humanDecisionNodeId,
  humanEvidenceNodeId,
  humanJournalNodeId,
  parseHumanNodeId,
  suppressV2HumanNodes,
} from "./humanAdapter";
import {
  ALL_HUMAN_RECORD_KINDS,
  DEFAULT_HUMAN_FILTERS,
  humanFilterCount,
  parseHumanFilterState,
  updateHumanFilterUrl,
  type HumanFilterState,
} from "./humanViewState";
import { buildHumanFacetBaseRequest, buildHumanProvenanceRequest } from "./humanWorkspace";
import { useHumanProvenance, useHumanProvenanceFacets } from "./useHumanProvenance";
import { useJournalEntryWriteCapability } from "./useJournalEntryWriting";
import {
  visibleJournalDecisionOptions,
  visibleJournalSensitivityTiers,
} from "./journalWorkspace";
import { useImpactAnalysis } from "./useImpactAnalysis";
import {
  buildImpactPresentation,
  decorateImpactContextGraph,
  decorateImpactGraph,
} from "./impactPresentation";
import { ProofTracePanel } from "./ProofTracePanel";
import { TemporalLineageControl } from "./TemporalLineageControl";
import { TemporalComparePanel } from "./TemporalComparePanel";
import {
  parseEffectiveAsOfInput,
  parseTemporalCompareWindowInput,
  parseTemporalReplayInput,
  type TemporalSnapshotCut,
} from "./temporalState";
import {
  MODEL_CLASSIFICATION_LABELS,
  ModelClassificationBadge,
  NodeModelClassificationBadge,
  exactModelInstanceForNode,
  exactVisibleGenericModel,
  exactVisibleModelInstances,
  visibleModelClassification,
} from "./ModelClassificationBadge";
import "./dag-viewer.css";
import "./dag-workspace.css";

export type DagViewerVariant = "full" | "embedded" | "compact";

function isRootedViewMode(mode: DagViewMode): mode is "focus" | "impact" | "compare" | "time_compare" {
  return mode === "focus" || mode === "impact" || mode === "compare" || mode === "time_compare";
}

export type DagViewerCommandAction =
  | { type: "set-search"; query: string; isolateMatches?: boolean }
  | { type: "set-filter"; patch: Partial<DagFilterState> }
  | { type: "reset-filters" }
  | { type: "set-category-labels"; visible: boolean }
  | { type: "set-label-scale"; scale: number }
  | { type: "adjust-label-scale"; delta: number }
  | { type: "set-category-visibility"; category: { key?: string; label: string }; visible: boolean }
  | { type: "show-all-categories" }
  | { type: "set-category-importance"; category: { key?: string; label: string }; importance: DagCategoryImportance }
  | { type: "move-category"; category: { key?: string; label: string }; destination: "up" | "down" | "first" | "last" }
  | { type: "fit-view" }
  | { type: "run-benchmark" }
  | { type: "reset-viewer" };

export interface DagViewerCommandRequest {
  id: number;
  actions: readonly DagViewerCommandAction[];
}

export interface DagViewerGroundingContext {
  rootRef: { entityKind: "artifact_version"; id: string };
  cut: TemporalSnapshotCut;
  comparisonWindow: {
    beforeAsOf: string;
    afterAsOf: string;
  } | null;
}

export interface DagViewerGroundedHighlight {
  citationId: string;
  sourceLocator: string;
  recordKind: string;
  recordId: string;
  secondaryRecordId: string | null;
}

interface DagViewerGroundedNavigationBase {
  id: number;
  rootRef: { entityKind: "artifact_version"; id: string };
  cut: TemporalSnapshotCut;
  expectedSourceSemanticDigest: string;
  highlight: DagViewerGroundedHighlight | null;
}

export type DagViewerGroundedNavigationRequest =
  | (DagViewerGroundedNavigationBase & {
      targetView: "proof_trace";
      sourceRequest: GroundedProofTraceSourceRequest;
      comparisonWindow: null;
    })
  | (DagViewerGroundedNavigationBase & {
      targetView: "impact";
      sourceRequest: GroundedImpactSourceRequest;
      comparisonWindow: null;
    })
  | (DagViewerGroundedNavigationBase & {
      targetView: "human_provenance";
      sourceRequest: GroundedHumanSourceRequest;
      comparisonWindow: null;
    })
  | (DagViewerGroundedNavigationBase & {
      targetView: "time_compare";
      sourceRequest: GroundedTemporalCompareSourceRequest;
      comparisonWindow: { beforeAsOf: string; afterAsOf: string };
    });

function proofRequestFromGrounded(source: GroundedProofTraceSourceRequest): ProofTraceRequest {
  const { source_request_kind: _kind, ...request } = source;
  return request;
}

function impactRequestFromGrounded(source: GroundedImpactSourceRequest): ImpactAnalysisRequest {
  const { source_request_kind: _kind, ...request } = source;
  return request;
}

function temporalRequestFromGrounded(source: GroundedTemporalCompareSourceRequest): TemporalCompareRequest {
  const { source_request_kind: _kind, ...request } = source;
  return request;
}

function humanRequestFromGrounded(source: GroundedHumanSourceRequest): HumanProvenanceRequest {
  const { source_request_kind: _kind, ...request } = source;
  return request;
}

export interface DagViewerSurfaceProps {
  projectId: string;
  projectName?: string;
  branch?: string;
  selectedNodeId?: string | null;
  onSelectNode?: (nodeId: string | null) => void;
  onActivateNode?: (nodeId: string, node?: DagViewerNode) => void;
  syntheticNodeCount?: number | null;
  variant?: DagViewerVariant;
  onExpand?: () => void;
  dataOverride?: DAGData | null;
  enabled?: boolean;
  /** Replaces the multi-row viewer chrome with one task-oriented toolbar. */
  streamlined?: boolean;
  /** Optional controls rendered at the start of the streamlined toolbar. */
  toolbarLeading?: ReactNode;
  /** Optional search/agent control rendered in place of the built-in search. */
  toolbarQuery?: ReactNode;
  /** Deterministic UI actions, normally produced by the Lineage agent bar. */
  commandRequest?: DagViewerCommandRequest | null;
  /** Exact, authorized root/cut currently available for a grounded question. */
  onGroundingContextChange?: (context: DagViewerGroundingContext | null) => void;
  /** Typed evidence navigation. Kept separate from deterministic UI commands. */
  groundedNavigationRequest?: DagViewerGroundedNavigationRequest | null;
}

interface HoverCard {
  node: DagViewerNode;
  x: number;
  y: number;
}

const integerFormatter = new Intl.NumberFormat();
const PREFERENCES_KEY = "thread.dag-viewer.preferences.v1";

interface DagViewerPreferences extends DagCanvasPreferences {
  categoryOrder: string[];
  hiddenCategoryKeys: string[];
}

const DEFAULT_PREFERENCES: DagViewerPreferences = {
  showCategoryLabels: true,
  fontScale: 1,
  categoryOrder: [],
  hiddenCategoryKeys: [],
  importanceByCategory: {},
};

const importanceOptions: Array<{ value: DagCategoryImportance; label: string }> = [
  { value: "quiet", label: "Quiet" },
  { value: "standard", label: "Normal" },
  { value: "key", label: "Key" },
];

function clampFontScale(value: number): number {
  return Math.max(0.8, Math.min(1.6, Math.round(value * 10) / 10));
}

function loadPreferences(): DagViewerPreferences {
  if (typeof window === "undefined") return DEFAULT_PREFERENCES;
  try {
    const stored = JSON.parse(localStorage.getItem(PREFERENCES_KEY) ?? "null") as Partial<DagViewerPreferences> | null;
    if (!stored) return DEFAULT_PREFERENCES;
    return {
      showCategoryLabels: stored.showCategoryLabels !== false,
      fontScale: clampFontScale(typeof stored.fontScale === "number" ? stored.fontScale : 1),
      categoryOrder: Array.isArray(stored.categoryOrder) ? stored.categoryOrder.filter((key): key is string => typeof key === "string") : [],
      hiddenCategoryKeys: Array.isArray(stored.hiddenCategoryKeys) ? stored.hiddenCategoryKeys.filter((key): key is string => typeof key === "string") : [],
      importanceByCategory: stored.importanceByCategory && typeof stored.importanceByCategory === "object"
        ? stored.importanceByCategory
        : {},
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

function orderedCategories(categories: DagCategoryDefinition[], preferred: string[]): DagCategoryDefinition[] {
  const byKey = new Map(categories.map((category) => [category.key, category]));
  return [
    ...preferred.map((key) => byKey.get(key)).filter((category): category is DagCategoryDefinition => Boolean(category)),
    ...categories.filter((category) => !preferred.includes(category.key)),
  ];
}

function formatTimestamp(value: string | null): string {
  if (!value) return "timestamp unavailable";
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return value;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(parsed));
}

function shortDigest(value: string | null): string {
  if (!value) return "—";
  return value.length > 24 ? `${value.slice(0, 22)}…` : value;
}

type InspectableCanonicalRecord =
  | CanonicalArtifactRecord
  | CanonicalArtifactVersionRecord
  | CanonicalOperationSpecRecord
  | CanonicalToolSnapshotRecord
  | CanonicalActorRecord
  | CanonicalOperationRunRecord
  | CanonicalBindingRecord
  | CanonicalBindingStateEventRecord
  | CanonicalOperationStateEventRecord
  | CanonicalSupersessionRecord
  | CanonicalMergeInputRecord
  | CanonicalLifecycleEventRecord
  | CanonicalDecisionRecord
  | CanonicalDecisionEvidenceRecord
  | CanonicalDecisionStatusEventRecord
  | CanonicalJournalEntryRecord;

const inspectableCanonicalKinds = new Set<InspectableCanonicalRecord["entity_kind"]>([
  "artifact", "artifact_version", "operation_spec", "tool", "actor", "operation_run", "binding",
  "binding_state_event", "operation_state_event", "supersession", "merge_input", "lifecycle_event",
  "decision", "decision_evidence", "decision_status_event", "journal_entry",
]);

function canonicalRecordIdentity(record: InspectableCanonicalRecord): string {
  return record.entity_kind === "decision_evidence" ? `${record.decision_id}:${record.ordinal}` : record.id;
}

/** Exact records survive canonical adaptation under metadata.canonical_record. */
function exactCanonicalRecord(node: DagViewerNode): InspectableCanonicalRecord | null {
  const value = node.metadata.canonical_record;
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as { entity_kind?: unknown };
  if (typeof record.entity_kind !== "string" || !inspectableCanonicalKinds.has(record.entity_kind as InspectableCanonicalRecord["entity_kind"])) return null;
  const canonicalRecord = value as InspectableCanonicalRecord;
  if (canonicalRecordIdentity(canonicalRecord) !== node.metadata.canonical_id || record.entity_kind !== node.metadata.canonical_entity_kind) return null;
  return canonicalRecord;
}

function exactArtifactVersion(node: DagViewerNode): CanonicalArtifactVersionRecord | null {
  const record = exactCanonicalRecord(node);
  return record?.entity_kind === "artifact_version" ? record : null;
}

interface CanonicalSemanticRow { label: string; value: string }

function semanticRows(record: InspectableCanonicalRecord): CanonicalSemanticRow[] {
  switch (record.entity_kind) {
    case "artifact": return [
      { label: "Logical identity", value: record.logical_id },
      { label: "Artifact kind", value: record.kind },
      { label: "Name", value: record.name },
      { label: "Creator", value: record.created_by },
      { label: "Created", value: record.created_at },
      { label: "Model classification", value: record.model_instance.classification },
      ...(record.model_instance.classification === "instance" ? [{
        label: "Generic Artifact",
        value: record.model_instance.generic_artifact_id,
      }] : []),
    ];
    case "artifact_version": return [
      { label: "Content digest", value: `${record.content_identity.algorithm}:${record.content_identity.digest}` },
      { label: "Media type", value: record.content_identity.media_type },
      { label: "Bytes", value: record.content_identity.byte_length == null ? "Not recorded" : integerFormatter.format(record.content_identity.byte_length) },
      { label: "Lifecycle", value: record.lifecycle_state },
      { label: "Schema", value: record.schema_id ?? "Not declared" },
      { label: "Unit context", value: record.unit_context_id ?? "Not declared" },
      { label: "Coordinate frame", value: record.coordinate_frame_id ?? "Not declared" },
    ];
    case "operation_spec": return [
      { label: "Definition", value: `${record.name} v${record.version}` },
      { label: "Operation type", value: record.operation_type },
      { label: "Definition hash", value: record.definition_hash },
      { label: "Input roles", value: integerFormatter.format(record.input_roles.length) },
      { label: "Output roles", value: integerFormatter.format(record.output_roles.length) },
    ];
    case "tool": return [
      { label: "Definition", value: `${record.name} ${record.version}` },
      { label: "Build", value: record.build_id ?? "Not recorded" },
      { label: "Definition hash", value: record.definition_hash },
      { label: "Source", value: record.source_ref ?? "Not recorded" },
    ];
    case "operation_run": return [
      { label: "Invocation status", value: record.status },
      { label: "OperationSpec", value: record.operation_spec_id },
      { label: "Tool", value: record.tool_id },
      { label: "Actor", value: record.actor_id },
      { label: "Started", value: record.started_at },
      { label: "Completed", value: record.completed_at },
      { label: "Configuration", value: record.config_hash ?? "Not recorded" },
    ];
    case "binding": return [
      { label: "Direction", value: record.direction },
      { label: "Role", value: `${record.role} · ordinal ${record.ordinal}` },
      { label: "OperationRun", value: record.operation_id },
      { label: "ArtifactVersion", value: record.artifact_version_id },
      { label: "State", value: `${record.state.state} · ${record.state.basis}` },
      { label: "Propagates invalidity", value: record.propagates_invalidity ? "Yes" : "No" },
    ];
    case "binding_state_event": return [
      { label: "Binding", value: record.binding_id },
      { label: "OperationRun", value: record.operation_id ?? "Not recorded" },
      { label: "State", value: record.state },
      { label: "Change cause", value: record.change_cause },
      { label: "Actor", value: record.actor_id },
      { label: "Effective / event", value: record.effective_at ?? "Not recorded" },
      { label: "Recorded / system", value: record.recorded_at },
      { label: "Updated", value: record.updated_at },
      { label: "Reason", value: record.reason ?? "Not recorded" },
    ];
    case "operation_state_event": return [
      { label: "OperationRun", value: record.operation_id },
      { label: "State", value: record.state },
      { label: "Change cause", value: record.change_cause },
      { label: "Actor", value: record.actor_id },
      { label: "Effective / event", value: record.effective_at ?? "Not recorded" },
      { label: "Recorded / system", value: record.recorded_at },
      { label: "Updated", value: record.updated_at },
      { label: "Reason", value: record.reason ?? "Not recorded" },
    ];
    case "supersession": return [
      { label: "Previous version", value: record.old_artifact_version_id },
      { label: "New version", value: record.new_artifact_version_id },
      { label: "Change type", value: record.change_type },
      { label: "OperationRun", value: record.operation_id ?? "Not recorded" },
      { label: "Creator", value: record.created_by },
      { label: "Created", value: record.created_at },
      { label: "Reason", value: record.reason ?? "Not recorded" },
    ];
    case "merge_input": return [
      { label: "Merge operation", value: record.merge_operation_id },
      { label: "Source operation", value: record.source_operation_id },
      { label: "Included", value: record.included ? "Yes" : "No" },
      { label: "Rationale", value: record.rationale ?? "Not recorded" },
      { label: "Created", value: record.created_at },
    ];
    case "actor": return [
      { label: "Actor type", value: record.actor_type },
      { label: "Resolution", value: record.resolution },
      { label: "Name", value: record.name ?? "Withheld or not resolved" },
      { label: "Role", value: record.role ?? "Not recorded" },
    ];
    case "lifecycle_event": return [
      { label: "ArtifactVersion", value: record.artifact_version_id },
      { label: "Event", value: record.event_type },
      { label: "State", value: record.state },
      { label: "Caused by version", value: record.caused_by_artifact_version_id ?? "Not recorded" },
      { label: "Effective / event", value: record.effective_at },
      { label: "Recorded / system", value: record.recorded_at },
      { label: "Actor", value: record.actor_id },
      { label: "Reason", value: record.reason },
    ];
    case "decision": return [
      { label: "Decision", value: record.decision_id },
      { label: "Status", value: record.status },
      { label: "Evidence", value: `${record.evidence_artifact_version_ids.length} ArtifactVersion${record.evidence_artifact_version_ids.length === 1 ? "" : "s"}` },
      { label: "Timestamp", value: record.timestamp },
      { label: "Thread URI", value: record.thread_uri },
      { label: "Rationale", value: record.rationale_spool_uri ?? "Not recorded" },
    ];
    case "decision_evidence": return [
      { label: "Decision", value: record.decision_id },
      { label: "Evidence ArtifactVersion", value: record.artifact_version_id },
      { label: "Ordinal", value: integerFormatter.format(record.ordinal) },
      { label: "Effective / event", value: record.effective_at },
      { label: "Recorded / system", value: record.recorded_at },
    ];
    case "decision_status_event": return [
      { label: "Decision", value: record.decision_id },
      { label: "Status", value: record.status },
      { label: "Actor", value: record.actor_id },
      { label: "Effective / event", value: record.effective_at },
      { label: "Recorded / system", value: record.recorded_at },
      { label: "Reason", value: record.reason ?? "Not recorded" },
    ];
    case "journal_entry": return [
      { label: "ArtifactVersion", value: record.artifact_version_id },
      { label: "Ordinal", value: integerFormatter.format(record.ordinal) },
      { label: "Actor", value: record.actor_id },
      { label: "Effective / event", value: record.effective_at },
      { label: "Recorded / system", value: record.recorded_at },
      { label: "Thread URI", value: record.thread_uri ?? "Not recorded" },
    ];
  }
}

function semanticRole(record: InspectableCanonicalRecord): "Definition" | "Invocation" | "Relation" | "State event" | "Evidence" | "Journal" | "Canonical record" {
  if (record.entity_kind === "operation_spec" || record.entity_kind === "tool") return "Definition";
  if (record.entity_kind === "operation_run") return "Invocation";
  if (record.entity_kind === "binding" || record.entity_kind === "supersession" || record.entity_kind === "merge_input") return "Relation";
  if (record.entity_kind === "binding_state_event" || record.entity_kind === "operation_state_event" || record.entity_kind === "lifecycle_event" || record.entity_kind === "decision_status_event") return "State event";
  if (record.entity_kind === "decision_evidence") return "Evidence";
  if (record.entity_kind === "journal_entry") return "Journal";
  return "Canonical record";
}

function dependencyRelationCount(
  graph: DagViewerGraph,
  startIndex: number,
  direction: "incoming" | "outgoing",
): number {
  const seen = new Set([startIndex]);
  const queue = [startIndex];
  while (queue.length) {
    const current = queue.shift()!;
    for (const edge of graph.edges) {
      if (edge.kind === "instance_of") continue;
      const next = direction === "incoming"
        ? edge.target === current ? edge.source : null
        : edge.source === current ? edge.target : null;
      if (next == null || seen.has(next)) continue;
      seen.add(next);
      queue.push(next);
    }
  }
  return Math.max(0, seen.size - 1);
}

function decorateCompareGraph(projection: DagCompareProjection): DagViewerGraph {
  return {
    ...projection.graph,
    nodes: projection.graph.nodes.map((node) => {
      const role = projection.roleById.get(node.id);
      return role
        ? {
            ...node,
            anchor: role === "anchor-a" || role === "anchor-b" || node.anchor,
            metadata: { ...node.metadata, dag_view_role: role },
          }
        : node;
    }),
  };
}

function decorateFocusGraph(projection: DagFocusProjection, anchorId: string): DagViewerGraph {
  return {
    ...projection.graph,
    nodes: projection.graph.nodes.map((node) => ({
      ...node,
      anchor: node.id === anchorId || node.anchor,
      metadata: {
        ...node.metadata,
        dag_view_role: node.id === anchorId ? "anchor-a" : "focus",
        dag_hops: projection.distances.get(node.id) ?? null,
      },
    })),
  };
}

function mapSourceMaskToDisplay(
  sourceGraph: DagViewerGraph,
  displayGraph: DagViewerGraph,
  sourceMask: Uint8Array | null,
  summaryProjection: DagSummaryProjection | null,
): Uint8Array | null {
  if (!sourceMask) return null;
  if (sourceGraph === displayGraph) return sourceMask;
  const result = new Uint8Array(displayGraph.nodes.length);
  displayGraph.nodes.forEach((node, displayIndex) => {
    const sourceIndex = sourceGraph.nodeIndexById.get(node.id);
    if (sourceIndex != null) {
      result[displayIndex] = sourceMask[sourceIndex] ? 1 : 0;
      return;
    }
    const memberIds = summaryProjection?.memberIdsByGroup.get(node.id) ?? [];
    result[displayIndex] = memberIds.some((id) => {
      const memberIndex = sourceGraph.nodeIndexById.get(id);
      return memberIndex != null && Boolean(sourceMask[memberIndex]);
    }) ? 1 : 0;
  });
  return result;
}

function displayNodeIdForSource(
  sourceId: string,
  displayGraph: DagViewerGraph,
  summaryProjection: DagSummaryProjection | null,
): string | null {
  if (displayGraph.nodeIndexById.has(sourceId)) return sourceId;
  return summaryProjection?.sourceToGroupId.get(sourceId) ?? null;
}

function NodeTooltip({ hover }: { hover: HoverCard }) {
  const modelClassification = visibleModelClassification(hover.node);
  return (
    <div className="dag-viewer__tooltip" style={{ left: hover.x, top: hover.y }} data-testid="dag-viewer-tooltip">
      <span>{hover.node.type} · {hover.node.kind} · {hover.node.state ?? "recorded"}</span>
      <strong>{hover.node.label}</strong>
      {modelClassification ? <small>{MODEL_CLASSIFICATION_LABELS[modelClassification.classification]}</small> : null}
      <small>{formatTimestamp(hover.node.timestamp)}</small>
    </div>
  );
}

function CategoryMixer({
  categories,
  categoryCounts,
  preferences,
  onClose,
  onFontScale,
  onImportance,
  onLabels,
  onMove,
  onReset,
  onShowAll,
  onVisibility,
}: {
  categories: DagCategoryDefinition[];
  categoryCounts: Map<string, number>;
  preferences: DagViewerPreferences;
  onClose: () => void;
  onFontScale: (value: number) => void;
  onImportance: (key: string, importance: DagCategoryImportance) => void;
  onLabels: (visible: boolean) => void;
  onMove: (key: string, direction: -1 | 1) => void;
  onReset: () => void;
  onShowAll: () => void;
  onVisibility: (key: string, visible: boolean) => void;
}) {
  const hidden = new Set(preferences.hiddenCategoryKeys);
  return (
    <aside className="dag-viewer__mixer" aria-label="Category mixer" data-testid="dag-viewer-category-mixer">
      <header>
        <div><span>View controls</span><h2>Category mixer</h2></div>
        <button type="button" onClick={onClose} aria-label="Close category mixer">×</button>
      </header>
      <p>Order the lanes, cut noise, and pull the records that matter into focus.</p>
      <section className="dag-viewer__mixer-display" aria-label="Display settings">
        <label className="dag-viewer__switch">
          <span>Left-side labels<small>Show category names on the canvas</small></span>
          <input type="checkbox" checked={preferences.showCategoryLabels} onChange={(event) => onLabels(event.target.checked)} />
          <i aria-hidden="true" />
        </label>
        <div className="dag-viewer__text-scale">
          <span>Graph text<small>Lane, date, and card labels</small></span>
          <div>
            <button type="button" onClick={() => onFontScale(preferences.fontScale - 0.1)} aria-label="Decrease graph text size">A−</button>
            <input
              type="range"
              min="0.8"
              max="1.6"
              step="0.1"
              value={preferences.fontScale}
              onChange={(event) => onFontScale(Number(event.target.value))}
              aria-label="Graph text size"
            />
            <button type="button" onClick={() => onFontScale(preferences.fontScale + 0.1)} aria-label="Increase graph text size">A+</button>
            <output>{Math.round(preferences.fontScale * 100)}%</output>
          </div>
        </div>
      </section>
      <div className="dag-viewer__mixer-heading">
        <span>Categories</span><small>visibility · emphasis · order</small>
      </div>
      <ol className="dag-viewer__category-list">
        {categories.map((category, index) => {
          const visible = !hidden.has(category.key);
          const importance = preferences.importanceByCategory[category.key] ?? "standard";
          return (
            <li key={category.key} className={`${visible ? "" : "is-hidden"} is-${importance}`}>
              <button
                type="button"
                className="dag-viewer__category-visibility"
                onClick={() => onVisibility(category.key, !visible)}
                aria-label={`${visible ? "Hide" : "Show"} ${category.label}`}
                aria-pressed={visible}
                title={`${visible ? "Hide" : "Show"} category`}
              >
                <i aria-hidden="true" />
              </button>
              <div className="dag-viewer__category-name">
                <strong>{category.label}</strong>
                <small>{integerFormatter.format(categoryCounts.get(category.key) ?? 0)} records</small>
              </div>
              <div className="dag-viewer__importance" aria-label={`${category.label} importance`}>
                {importanceOptions.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={importance === option.value ? "is-active" : ""}
                    aria-pressed={importance === option.value}
                    onClick={() => onImportance(category.key, option.value)}
                  >{option.label}</button>
                ))}
              </div>
              <div className="dag-viewer__category-order">
                <button type="button" disabled={index === 0} onClick={() => onMove(category.key, -1)} aria-label={`Move ${category.label} up`}>↑</button>
                <button type="button" disabled={index === categories.length - 1} onClick={() => onMove(category.key, 1)} aria-label={`Move ${category.label} down`}>↓</button>
              </div>
            </li>
          );
        })}
      </ol>
      <footer>
        <button type="button" onClick={onShowAll}>Show all</button>
        <button type="button" onClick={onReset}>Reset view</button>
      </footer>
    </aside>
  );
}

const recordTypeLabels: Record<(typeof ALL_DAG_RECORD_TYPES)[number], string> = {
  artifact: "Artifacts",
  version: "Versions",
  operation: "Operations",
  human: "Human",
  decision: "Decisions",
  lifecycle: "Lifecycle",
};

const pathStateLabels: Record<(typeof ALL_DAG_PATH_STATES)[number], string> = {
  active: "Active",
  human: "Human trail",
  invalidated: "Invalidated",
  context: "Context",
};

const topologyLabels: Record<DagTopologyFacet, string> = {
  root: "Roots",
  leaf: "Leaves",
  anchor: "Story anchors",
};

interface DagFacetCounts {
  recordTypes: Map<string, number>;
  pathStates: Map<string, number>;
  topology: Map<string, number>;
}

function toggledValue<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((candidate) => candidate !== value) : [...values, value];
}

function withSafeModelClassifications(filters: DagFilterState): DagFilterState {
  const modelClassifications = ALL_MODEL_CLASSIFICATIONS.filter((classification) => (
    filters.modelClassifications.includes(classification)
  ));
  return {
    ...filters,
    modelClassifications: modelClassifications.length
      ? modelClassifications
      : [...ALL_MODEL_CLASSIFICATIONS],
  };
}

function FilterPanel({
  counts,
  filters,
  serverBacked,
  onChange,
  onClose,
  onReset,
}: {
  counts: DagFacetCounts;
  filters: DagFilterState;
  serverBacked: boolean;
  onChange: (filters: DagFilterState) => void;
  onClose: () => void;
  onReset: () => void;
}) {
  const timeOptions: Array<{ value: DagTimeWindowDays; label: string }> = [
    { value: null, label: "All time" },
    { value: 7, label: "7 days" },
    { value: 30, label: "30 days" },
    { value: 90, label: "90 days" },
  ];
  return (
    <aside className="dag-viewer__filters" aria-label="Lineage filters" data-testid="dag-viewer-filter-panel">
      <header>
        <div><span>Discovery</span><h2>Filter lineage</h2></div>
        <button type="button" onClick={onClose} aria-label="Close lineage filters">×</button>
      </header>
      <section className="dag-viewer__filter-presets" aria-label="Quick filters">
        <span>Quick views</span>
        <div>
          <button type="button" onClick={() => onChange({ ...DEFAULT_DAG_FILTERS, pathStates: ["invalidated"] })}>Problems</button>
          <button type="button" onClick={() => onChange({ ...DEFAULT_DAG_FILTERS, timeWindowDays: 30 })}>Recent changes</button>
          {serverBacked ? (
            <button type="button" onClick={() => onChange({ ...DEFAULT_DAG_FILTERS, pathStates: ["active"] })}>Active only</button>
          ) : (
            <>
              <button type="button" onClick={() => onChange({ ...DEFAULT_DAG_FILTERS, pathStates: ["human"] })}>Human trail</button>
              <button type="button" onClick={() => onChange({ ...DEFAULT_DAG_FILTERS, topology: ["anchor"] })}>Anchors</button>
            </>
          )}
        </div>
      </section>
      {!serverBacked ? <section className="dag-viewer__filter-group">
        <header><span>Record type</span><small>OR within this group</small></header>
        <div className="dag-viewer__filter-options">
          {ALL_DAG_RECORD_TYPES.map((type) => {
            const active = filters.recordTypes.includes(type);
            return (
              <button
                type="button"
                key={type}
                className={active ? "is-active" : ""}
                aria-pressed={active}
                onClick={() => onChange({ ...filters, recordTypes: toggledValue(filters.recordTypes, type) })}
              >{recordTypeLabels[type]} <small>{integerFormatter.format(counts.recordTypes.get(type) ?? 0)}</small></button>
            );
          })}
        </div>
      </section> : null}
      <section className="dag-viewer__filter-group">
        <header><span>{serverBacked ? "Artifact lifecycle" : "Path state"}</span><small>{serverBacked ? "project-wide before pagination" : "normalized across records"}</small></header>
        <div className="dag-viewer__filter-options">
          {(serverBacked ? ["active", "invalidated"] as const : ALL_DAG_PATH_STATES).map((state) => {
            const active = filters.pathStates.includes(state);
            return (
              <button
                type="button"
                key={state}
                className={active ? "is-active" : ""}
                aria-pressed={active}
                onClick={() => onChange({ ...filters, pathStates: toggledValue(filters.pathStates, state) })}
              >{pathStateLabels[state]} {!serverBacked ? <small>{integerFormatter.format(counts.pathStates.get(state) ?? 0)}</small> : null}</button>
            );
          })}
        </div>
      </section>
      {serverBacked ? <section className="dag-viewer__filter-group dag-viewer__model-filter" data-testid="dag-model-classification-filter">
        <header><span>Model classification</span><small>explicit Artifact relation at the exact cut</small></header>
        <div className="dag-viewer__filter-options">
          {ALL_MODEL_CLASSIFICATIONS.map((classification) => {
            const active = filters.modelClassifications.includes(classification);
            const lastSelected = active && filters.modelClassifications.length === 1;
            return (
              <button
                type="button"
                key={classification}
                className={active ? "is-active" : ""}
                aria-pressed={active}
                disabled={lastSelected}
                title={lastSelected ? "At least one model classification must remain selected." : undefined}
                onClick={() => onChange({
                  ...filters,
                  modelClassifications: ALL_MODEL_CLASSIFICATIONS.filter((value) => (
                    value === classification ? !active : filters.modelClassifications.includes(value)
                  )),
                })}
              >{MODEL_CLASSIFICATION_LABELS[classification]}</button>
            );
          })}
        </div>
      </section> : null}
      {!serverBacked ? <section className="dag-viewer__filter-group">
        <header><span>Topology</span><small>roots, outcomes, landmarks</small></header>
        <div className="dag-viewer__filter-options">
          {(["root", "leaf", "anchor"] as DagTopologyFacet[]).map((facet) => {
            const active = filters.topology.includes(facet);
            return (
              <button
                type="button"
                key={facet}
                className={active ? "is-active" : ""}
                aria-pressed={active}
                onClick={() => onChange({ ...filters, topology: toggledValue(filters.topology, facet) })}
              >{topologyLabels[facet]} <small>{integerFormatter.format(counts.topology.get(facet) ?? 0)}</small></button>
            );
          })}
        </div>
      </section> : null}
      <section className="dag-viewer__filter-group">
        <header><span>Time window</span><small>{serverBacked ? "project-wide before pagination" : "relative to latest record"}</small></header>
        <div className="dag-viewer__filter-options is-single-choice">
          {timeOptions.map((option) => (
            <button
              type="button"
              key={option.label}
              className={filters.timeWindowDays === option.value ? "is-active" : ""}
              aria-pressed={filters.timeWindowDays === option.value}
              onClick={() => onChange({ ...filters, timeWindowDays: option.value })}
            >{option.label}</button>
          ))}
        </div>
      </section>
      <footer>
        <span>Groups combine with AND</span>
        <button type="button" onClick={onReset}>Clear filters</button>
      </footer>
    </aside>
  );
}

function DagDetailPanel({
  graph,
  nodeIndex,
  outsideFilters,
  onClose,
  onSelect,
  onActivate,
  onOpenProof,
  humanResult,
}: {
  graph: DagViewerGraph;
  nodeIndex: number;
  outsideFilters?: boolean;
  onClose: () => void;
  onSelect: (nodeId: string) => void;
  onActivate?: (nodeId: string) => void;
  onOpenProof?: () => void;
  humanResult?: import("../../api/humanProvenance").HumanProvenanceResult | null;
}) {
  const node = graph.nodes[nodeIndex]!;
  const canonicalRecord = exactCanonicalRecord(node);
  const artifactVersion = exactArtifactVersion(node);
  const ancestorCount = dependencyRelationCount(graph, nodeIndex, "incoming");
  const descendantCount = dependencyRelationCount(graph, nodeIndex, "outgoing");
  const neighbours = [...new Set([...(graph.incoming[nodeIndex] ?? []), ...(graph.outgoing[nodeIndex] ?? [])])]
    .slice(0, 6)
    .map((index) => graph.nodes[index]!);
  const modelContextNeighbourIds = new Set(graph.edges.flatMap((edge) => {
    if (edge.kind !== "instance_of") return [];
    if (edge.source === nodeIndex) return [graph.nodes[edge.target]!.id];
    if (edge.target === nodeIndex) return [graph.nodes[edge.source]!.id];
    return [];
  }));
  const expandableBoundary = node.metadata.boundary === true
    && node.metadata.boundary_reason === "outside_projection"
    && node.metadata.canonical_entity_kind === "artifact_version";
  const canActivate = onActivate && (node.metadata.boundary !== true || expandableBoundary);
  const modelInstance = exactModelInstanceForNode(node);
  const exactGenericTarget = exactVisibleGenericModel(graph, node);
  const visibleInstances = exactVisibleModelInstances(graph, node);
  return (
    <aside className="dag-viewer__detail" aria-label="Selected record details" data-testid="dag-viewer-detail">
      <header>
        <div><span>Selected record</span><h2>{node.label}</h2></div>
        <button type="button" onClick={onClose} aria-label="Close details">×</button>
      </header>
      <div className="dag-viewer__detail-badges">
        <span>{node.type}</span><span>{node.kind}</span><span className={`is-${node.pathState}`}>{node.state ?? "recorded"}</span>
        <NodeModelClassificationBadge node={node} />
      </div>
      {outsideFilters ? <p className="dag-viewer__outside-filters">Pinned selection · outside active filters</p> : null}
      <dl>
        <div><dt>Timestamp</dt><dd>{formatTimestamp(node.timestamp)}</dd></div>
        <div><dt>Digest</dt><dd title={node.digest ?? undefined}>{shortDigest(node.digest)}</dd></div>
        <div><dt>Dependency ancestors</dt><dd>{integerFormatter.format(ancestorCount)}</dd></div>
        <div><dt>Dependency descendants</dt><dd>{integerFormatter.format(descendantCount)}</dd></div>
      </dl>
      {canonicalRecord ? (
        <section className={`dag-viewer__semantic-inspector is-${semanticRole(canonicalRecord).toLocaleLowerCase().replace(" ", "-")}`} data-testid="dag-viewer-semantic-inspector">
          <header>
            <div><span>Exact canonical semantics</span><h3>{semanticRole(canonicalRecord)}</h3></div>
            <code>{canonicalRecord.entity_kind}</code>
          </header>
          <dl>
            {semanticRows(canonicalRecord).map((row) => (
              <div key={row.label}><dt>{row.label}</dt><dd title={row.value}>{row.value}</dd></div>
            ))}
          </dl>
        </section>
      ) : null}
      {modelInstance ? (
        <section className="dag-viewer__model-relation" aria-label="Model and instance relation" data-testid="dag-model-relation">
          <div className="dag-viewer__detail-eyebrow">Model / instance · context only</div>
          {modelInstance.classification === "instance" ? (
            exactGenericTarget ? (
              <button
                type="button"
                onClick={() => onSelect(exactGenericTarget.id)}
                aria-label={`Go to generic model ${exactGenericTarget.label}`}
              >
                <ModelClassificationBadge classification="generic" />
                <span><strong>{exactGenericTarget.label}</strong><small>Exact visible generic Artifact · non-propagating context</small></span>
              </button>
            ) : (
              <p role="status" data-testid="dag-model-target-boundary">
                Generic model unavailable in this bounded projection. No target was synthesized and traversal is disabled.
              </p>
            )
          ) : modelInstance.classification === "generic" ? (
            visibleInstances.length ? (
              <div className="dag-viewer__model-instance-list">
                {visibleInstances.map((instance) => (
                  <button
                    type="button"
                    key={instance.id}
                    onClick={() => onSelect(instance.id)}
                    aria-label={`Go to model instance ${instance.label}`}
                  >
                    <ModelClassificationBadge classification="instance" />
                    <span><strong>{instance.label}</strong><small>Exact visible instance Artifact · non-propagating context</small></span>
                  </button>
                ))}
              </div>
            ) : <p role="status">No model instances are visible in this bounded projection.</p>
          ) : (
            <p role="note">No visible generic or instance assertion is available at this exact cut. Nothing was inferred from Artifact kind, name, or metadata.</p>
          )}
        </section>
      ) : node.semanticRole === "artifact" && node.metadata.boundary !== true ? (
        <section className="dag-viewer__model-relation" aria-label="Model and instance relation">
          <div className="dag-viewer__detail-eyebrow">Model / instance · context only</div>
          <p role="note">Canonical model classification was not recorded; this Artifact is shown as Unclassified. Nothing was inferred from Artifact kind, name, or metadata.</p>
        </section>
      ) : null}
      <HumanRecordInspector
        node={node}
        result={humanResult ?? null}
        onSelect={onSelect}
        availableNodeIds={graph.nodeIndexById}
      />
      {neighbours.length ? (
        <section>
          <span className="dag-viewer__detail-eyebrow">Immediate context</span>
          <div className="dag-viewer__neighbours">
            {neighbours.map((neighbour) => (
              neighbour.metadata.interactive === false ? (
                <span className="dag-viewer__neighbour-boundary" key={neighbour.id} role="note">
                  <i className={`is-${neighbour.pathState}`} />
                  <span>{neighbour.label}<small>{neighbour.kind}{modelContextNeighbourIds.has(neighbour.id) ? " · model context only" : ""} · traversal unavailable</small></span>
                </span>
              ) : (
                <button type="button" key={neighbour.id} onClick={() => onSelect(neighbour.id)}>
                  <i className={`is-${neighbour.pathState}`} />
                  <span>{neighbour.label}<small>{neighbour.kind}{modelContextNeighbourIds.has(neighbour.id) ? " · model context only" : ""}</small></span>
                </button>
              )
            ))}
          </div>
        </section>
      ) : null}
      {canActivate ? (
        <button className="dag-viewer__open-record" type="button" onClick={() => onActivate?.(node.id)}>
          {expandableBoundary ? "Expand from this boundary" : "Open record ↗"}
        </button>
      ) : null}
      {artifactVersion && onOpenProof ? (
        <button className="dag-viewer__proof-record" data-testid="dag-viewer-proof-trace" type="button" onClick={onOpenProof}>
          Open proof trace
        </button>
      ) : null}
    </aside>
  );
}

export function DagViewerSurface({
  projectId,
  projectName = "Project lineage",
  selectedNodeId = null,
  onSelectNode,
  onActivateNode,
  syntheticNodeCount = null,
  variant = "full",
  onExpand,
  dataOverride,
  enabled = true,
  streamlined = false,
  toolbarLeading,
  toolbarQuery,
  commandRequest = null,
  onGroundingContextChange,
  groundedNavigationRequest = null,
}: DagViewerSurfaceProps) {
  const initialWorkspaceState = useMemo(
    () => typeof window === "undefined" ? parseDagWorkspaceState("") : parseDagWorkspaceState(window.location.search),
    [],
  );
  const observedUrlSearch = typeof window === "undefined" ? "" : window.location.search;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const surfaceRef = useRef<HTMLElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const rendererRef = useRef<DagCanvasRenderer | null>(null);
  const graphRef = useRef<DagViewerGraph | null>(null);
  const sourceGraphRef = useRef<DagViewerGraph | null>(null);
  const viewModeRef = useRef<DagViewMode>(variant === "compact" ? "chronicle" : initialWorkspaceState.mode);
  const compareARef = useRef<string | null>(initialWorkspaceState.compareA);
  const compareBRef = useRef<string | null>(initialWorkspaceState.compareB);
  const summaryMembersRef = useRef<Map<string, string[]>>(new Map());
  const lastLaidOutGraphRef = useRef<DagViewerGraph | null>(null);
  const lastObservedUrlSearchRef = useRef(observedUrlSearch);
  const dsmProjectIdRef = useRef(projectId);
  const workspaceUrlWriteInProgressRef = useRef(false);
  const lastCommandRequestIdRef = useRef<number | null>(null);
  const lastGroundedNavigationIdRef = useRef<number | null>(null);
  const lastGroundedHighlightIdRef = useRef<number | null>(null);
  const groundingContextCallbackRef = useRef(onGroundingContextChange);
  const workspaceStateRef = useRef(initialWorkspaceState);
  const temporalHistoryCommitRef = useRef<"replace" | "push" | null>(
    initialWorkspaceState.temporalError === null
      && initialWorkspaceState.recordedAsOf === null
      && initialWorkspaceState.snapshotToken === null
      ? "replace"
      : null,
  );
  const dsmHistoryCommitRef = useRef<"push" | null>(null);
  const selectCallbackRef = useRef(onSelectNode);
  const activateCallbackRef = useRef(onActivateNode);
  const bucketActivateRef = useRef<(node: DagViewerNode) => void>(() => undefined);
  const boundaryActivateRef = useRef<(node: DagViewerNode) => boolean>(() => false);
  const [hover, setHover] = useState<HoverCard | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(selectedNodeId);
  const [viewMode, setViewMode] = useState<DagViewMode>(variant === "compact" ? "chronicle" : initialWorkspaceState.mode);
  const [direction, setDirection] = useState(initialWorkspaceState.direction);
  const [depth, setDepth] = useState<number | null>(initialWorkspaceState.depth);
  const [compareA, setCompareA] = useState<string | null>(initialWorkspaceState.compareA);
  const [compareB, setCompareB] = useState<string | null>(initialWorkspaceState.compareB);
  const [timeCompareBeforeInput, setTimeCompareBeforeInput] = useState(initialWorkspaceState.timeCompareBeforeInput);
  const [timeCompareAfterInput, setTimeCompareAfterInput] = useState(initialWorkspaceState.timeCompareAfterInput);
  const [summaryGroup, setSummaryGroup] = useState<DagSummaryGroup>(initialWorkspaceState.summaryGroup);
  const [dsmMatrixKind, setDsmMatrixKind] = useState(initialWorkspaceState.dsmMatrixKind);
  const [dsmDrillToken, setDsmDrillToken] = useState<string | null>(initialWorkspaceState.dsmDrillToken);
  const [dsmCursor, setDsmCursor] = useState<string | null>(initialWorkspaceState.dsmCursor);
  const [dsmNavigationError, setDsmNavigationError] = useState<string | null>(initialWorkspaceState.dsmNavigationError);
  const [dsmProofRef, setDsmProofRef] = useState<LineageDsmArtifactVersionRef | null>(null);
  const [selectedSummaryGroupId, setSelectedSummaryGroupId] = useState<string | null>(null);
  const [recordBrowserOpen, setRecordBrowserOpen] = useState(false);
  const [storyMode, setStoryMode] = useState(false);
  const [layoutProgress, setLayoutProgress] = useState(0);
  const [layoutDuration, setLayoutDuration] = useState<number | null>(null);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [firstPaintMs, setFirstPaintMs] = useState<number | null>(null);
  const [benchmark, setBenchmark] = useState<DagFrameBenchmark | null>(null);
  const [benchmarking, setBenchmarking] = useState(false);
  const [mixerOpen, setMixerOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [humanFilterOpen, setHumanFilterOpen] = useState(false);
  const [journalFormContext, setJournalFormContext] = useState<string | null>(null);
  const [journalWriteNotice, setJournalWriteNotice] = useState<string | null>(null);
  const [filters, setFilters] = useState<DagFilterState>(() => ({
    ...initialWorkspaceState.filters,
    recordTypes: [...initialWorkspaceState.filters.recordTypes],
    pathStates: [...initialWorkspaceState.filters.pathStates],
    modelClassifications: [...initialWorkspaceState.filters.modelClassifications],
    topology: [...initialWorkspaceState.filters.topology],
  }));
  const [humanFilters, setHumanFilters] = useState<HumanFilterState>(() =>
    typeof window === "undefined" ? { ...DEFAULT_HUMAN_FILTERS } : parseHumanFilterState(window.location.search));
  const [searchQuery, setSearchQuery] = useState(initialWorkspaceState.searchQuery);
  const [settledSearchQuery, setSettledSearchQuery] = useState(initialWorkspaceState.searchQuery);
  // The text field is a draft. Only Apply/Now/history may change the applied
  // request, which prevents keystrokes from issuing temporal API calls or
  // rewriting a reproducible URL.
  const [effectiveAsOfInput, setEffectiveAsOfInput] = useState(initialWorkspaceState.asOfInput);
  const [appliedEffectiveAsOfInput, setAppliedEffectiveAsOfInput] = useState(initialWorkspaceState.asOfInput);
  const [appliedRecordedAsOfInput, setAppliedRecordedAsOfInput] = useState<string | null>(initialWorkspaceState.recordedAsOfInput);
  const [appliedSnapshotTokenInput, setAppliedSnapshotTokenInput] = useState<string | null>(initialWorkspaceState.snapshotTokenInput);
  const [searchResultsOpen, setSearchResultsOpen] = useState(false);
  const [activeMatchPosition, setActiveMatchPosition] = useState<number | null>(null);
  const [searchIsolate, setSearchIsolate] = useState(false);
  const [preferences, setPreferences] = useState<DagViewerPreferences>(loadPreferences);
  const [openedProofContext, setOpenedProofContext] = useState<string | null>(null);
  const [activeGroundedReplay, setActiveGroundedReplay] = useState<DagViewerGroundedNavigationRequest | null>(null);
  const [groundedHumanDigestState, setGroundedHumanDigestState] = useState<"idle" | "checking" | "verified" | "mismatch">("idle");

  const temporalDraft = useMemo(
    () => parseEffectiveAsOfInput(effectiveAsOfInput),
    [effectiveAsOfInput],
  );
  const appliedTemporalInput = useMemo(
    () => parseTemporalReplayInput(
      appliedEffectiveAsOfInput,
      appliedRecordedAsOfInput,
      appliedSnapshotTokenInput,
    ),
    [appliedEffectiveAsOfInput, appliedRecordedAsOfInput, appliedSnapshotTokenInput],
  );
  const timeCompareWindow = useMemo(
    () => parseTemporalCompareWindowInput(
      timeCompareBeforeInput,
      timeCompareAfterInput,
      appliedTemporalInput.recordedAsOf,
    ),
    [appliedTemporalInput.recordedAsOf, timeCompareAfterInput, timeCompareBeforeInput],
  );
  const asOf = appliedTemporalInput.normalized;
  const temporalDraftMatchesApplied = temporalDraft.error === null
    && appliedTemporalInput.error === null
    && temporalDraft.requestsNow === appliedTemporalInput.requestsNow
    && temporalDraft.normalized === appliedTemporalInput.normalized;
  const temporalRequestsEnabled = temporalDraftMatchesApplied && appliedTemporalInput.error === null;

  const syntheticData = useMemo(
    () => syntheticNodeCount ? generateSyntheticDag(syntheticNodeCount) : null,
    [syntheticNodeCount],
  );
  const usesCanonicalSource = dataOverride === undefined && !syntheticData;
  const dsmActive = variant !== "compact" && viewMode === "dsm";
  useEffect(() => {
    const timeout = window.setTimeout(() => setSettledSearchQuery(searchQuery), 250);
    return () => window.clearTimeout(timeout);
  }, [searchQuery]);
  useEffect(() => {
    if (dsmProjectIdRef.current === projectId) return;
    dsmProjectIdRef.current = projectId;
    setDsmDrillToken(null);
    setDsmCursor(null);
    setDsmNavigationError(null);
    dsmHistoryCommitRef.current = null;
  }, [projectId]);
  const selectedUsesHumanPresentation = Boolean(
    parseHumanNodeId(selectedId)
      || (selectedId?.startsWith("decision:") && selectedId.endsWith(":restricted-evidence")),
  );
  const dsmSearchSettled = searchQuery === settledSearchQuery;
  const lineageDsm = useLineageDsm({
    projectId,
    matrixKind: dsmMatrixKind,
    drillToken: dsmDrillToken,
    cursor: dsmCursor,
    selectedId,
    searchQuery: settledSearchQuery,
    filters,
    asOf,
    recordedAsOf: appliedTemporalInput.recordedAsOf,
    snapshotToken: appliedTemporalInput.snapshotToken,
  }, enabled && temporalRequestsEnabled && Boolean(projectId) && usesCanonicalSource && dsmActive && dsmNavigationError === null && dsmSearchSettled);
  const canonicalProjection = useCanonicalLineageProjection({
    projectId,
    mode: variant === "compact"
      ? "chronicle"
      : selectedUsesHumanPresentation && isRootedViewMode(viewMode) ? "chronicle" : viewMode,
    direction,
    depth,
    selectedId: selectedUsesHumanPresentation ? null : selectedId,
    compareA,
    compareB,
    summaryGroup,
    searchQuery: settledSearchQuery,
    filters,
    asOf,
    recordedAsOf: appliedTemporalInput.recordedAsOf,
    snapshotToken: appliedTemporalInput.snapshotToken,
  }, enabled && temporalRequestsEnabled && Boolean(projectId) && usesCanonicalSource && !dsmActive);
  const projectionTemporalCut = useMemo<TemporalSnapshotCut | null>(() => {
    const projection = canonicalProjection.projection;
    return projection ? {
      effectiveAsOf: projection.effectiveAsOf,
      recordedAsOf: projection.recordedAsOf,
      snapshotToken: projection.snapshotToken,
    } : null;
  }, [canonicalProjection.projection]);
  const issuedTemporalCut = dsmActive ? lineageDsm.issuedCut : projectionTemporalCut;
  const appliedCutIsExact = issuedTemporalCut !== null
    && asOf === issuedTemporalCut.effectiveAsOf
    && appliedTemporalInput.recordedAsOf === issuedTemporalCut.recordedAsOf
    && appliedTemporalInput.snapshotToken === issuedTemporalCut.snapshotToken;
  // An unbound freeze response supplies the trio that starts the exact
  // rebound request. React Query may still expose that response (or an older
  // cached exact response) while the rebound is in flight, so field equality
  // alone is not a display/URL commit boundary. Pagination is the one fetch
  // that may safely retain the already authenticated first page.
  const appliedCutIsSettledExact = appliedCutIsExact && (dsmActive
    ? lineageDsm.isExactSettled
    : !canonicalProjection.isPending
      && (!canonicalProjection.isFetching || canonicalProjection.isFetchingNextPage)
      && canonicalProjection.error === null);
  const activeTemporalPending = dsmActive ? lineageDsm.isPending : canonicalProjection.isPending;
  const activeTemporalFetching = dsmActive ? lineageDsm.isFetching : canonicalProjection.isFetching;
  const activeTemporalError = dsmActive ? lineageDsm.error : canonicalProjection.error;
  useEffect(() => {
    if (
      !usesCanonicalSource
      || !issuedTemporalCut
      || !temporalRequestsEnabled
      || appliedCutIsExact
      || activeTemporalPending
      || activeTemporalFetching
      || activeTemporalError !== null
    ) return;
    // The strict response parser already binds a caller-supplied effective
    // instant. Repeat the guard here before turning a first-page freeze into a
    // replayable browser state.
    if (asOf !== null && issuedTemporalCut.effectiveAsOf !== asOf) return;
    setEffectiveAsOfInput(issuedTemporalCut.effectiveAsOf);
    setAppliedEffectiveAsOfInput(issuedTemporalCut.effectiveAsOf);
    setAppliedRecordedAsOfInput(issuedTemporalCut.recordedAsOf);
    setAppliedSnapshotTokenInput(issuedTemporalCut.snapshotToken);
  }, [
    activeTemporalError,
    activeTemporalFetching,
    activeTemporalPending,
    appliedCutIsExact,
    asOf,
    issuedTemporalCut,
    temporalRequestsEnabled,
    usesCanonicalSource,
  ]);
  useEffect(() => {
    if (
      viewMode !== "time_compare"
      || timeCompareAfterInput.trim()
      || !appliedCutIsSettledExact
      || !projectionTemporalCut
    ) return;
    setTimeCompareAfterInput(projectionTemporalCut.effectiveAsOf);
  }, [appliedCutIsSettledExact, projectionTemporalCut, timeCompareAfterInput, viewMode]);
  selectCallbackRef.current = onSelectNode;
  activateCallbackRef.current = onActivateNode;
  groundingContextCallbackRef.current = onGroundingContextChange;
  bucketActivateRef.current = (node) => {
    const representativeRoot = typeof node.metadata.representative_root === "string"
      ? node.metadata.representative_root
      : null;
    setHover(null);
    setSelectedSummaryGroupId(null);
    setDsmDrillToken(null);
    setDsmCursor(null);
    setDsmNavigationError(null);
    dsmHistoryCommitRef.current = null;
    if (representativeRoot) {
      setSearchQuery("");
      setSettledSearchQuery("");
      setFilters({
        ...DEFAULT_DAG_FILTERS,
        recordTypes: [...DEFAULT_DAG_FILTERS.recordTypes],
        pathStates: [...DEFAULT_DAG_FILTERS.pathStates],
        modelClassifications: [...DEFAULT_DAG_FILTERS.modelClassifications],
        topology: [...DEFAULT_DAG_FILTERS.topology],
      });
      setViewMode("focus");
      setSelectedId(representativeRoot);
      selectCallbackRef.current?.(representativeRoot);
      return;
    }
    const bucket = node.metadata.projection_bucket as { dimension?: unknown; key?: unknown } | undefined;
    if (typeof bucket?.key === "string") {
      setSearchQuery(`${bucket.dimension === "lifecycle_state" ? "state" : "kind"}:${bucket.key}`);
      setViewMode("chronicle");
      setSelectedId(null);
      selectCallbackRef.current?.(null);
    }
  };
  boundaryActivateRef.current = (node) => {
    if (node.metadata.boundary !== true) return false;
    if (
      node.metadata.boundary_reason === "outside_projection"
      && node.metadata.canonical_entity_kind === "artifact_version"
      && typeof node.metadata.canonical_id === "string"
    ) {
      const rootId = `artifact_version:${node.metadata.canonical_id}`;
      setHover(null);
      setSearchQuery("");
      setSettledSearchQuery("");
      setFilters({
        ...DEFAULT_DAG_FILTERS,
        recordTypes: [...DEFAULT_DAG_FILTERS.recordTypes],
        pathStates: [...DEFAULT_DAG_FILTERS.pathStates],
        modelClassifications: [...DEFAULT_DAG_FILTERS.modelClassifications],
        topology: [...DEFAULT_DAG_FILTERS.topology],
      });
      setViewMode("focus");
      setSelectedId(rootId);
      selectCallbackRef.current?.(rootId);
    }
    // Missing and unsupported boundaries are explicit terminal records, not
    // fake IDs to send through the host's record opener.
    return true;
  };
  const data = syntheticData ?? (dataOverride !== undefined ? dataOverride : null);
  const localGraph = useMemo(() => data ? normalizeDagData(data) : null, [data]);
  const compatibilityHistoricalRequested = !usesCanonicalSource && (
    !appliedTemporalInput.requestsNow
    || appliedRecordedAsOfInput !== null
    || appliedSnapshotTokenInput !== null
  );
  const compatibilityTemporalError = compatibilityHistoricalRequested && appliedTemporalInput.error === null
    ? "This compatibility lineage source is current-only and cannot replay a historical temporal cut."
    : null;
  // A first unbound response exists only to freeze a replay trio. It must not
  // become visible: its relative-window bounds may have been derived before
  // the server effective cut was known, and a previous query may still be in
  // the hook cache. Only the exact rebound query is displayable downstream.
  const canonicalCutDisplayable = usesCanonicalSource && !dsmActive && appliedCutIsSettledExact;
  const displayedCanonicalProjection = canonicalCutDisplayable ? canonicalProjection.projection : null;
  const technicalDag = useMemo(
    () => displayedCanonicalProjection && canonicalProjection.graph ? {
      nodes: canonicalProjection.graph.nodes.map((node) => node.raw),
      edges: canonicalProjection.graph.edges.map((edge) => edge.raw),
    } : null,
    [canonicalProjection.graph, displayedCanonicalProjection],
  );
  const previousSelectedNode = selectedId && sourceGraphRef.current
    ? sourceGraphRef.current.nodes[sourceGraphRef.current.nodeIndexById.get(selectedId) ?? -1] ?? null
    : null;
  const groundedHumanSource = activeGroundedReplay?.targetView === "human_provenance"
    ? activeGroundedReplay.sourceRequest
    : null;
  const humanRequest = useMemo(() => (
    groundedHumanSource
      ? humanRequestFromGrounded(groundedHumanSource)
      : canonicalCutDisplayable && projectionTemporalCut && technicalDag
      ? buildHumanProvenanceRequest({
          projectId,
          selectedId,
          selectedNode: previousSelectedNode,
          searchQuery: settledSearchQuery,
          filters: humanFilters,
          cut: projectionTemporalCut,
        })
      : null
  ), [
    canonicalCutDisplayable,
    groundedHumanSource,
    humanFilters,
    previousSelectedNode,
    projectId,
    projectionTemporalCut,
    selectedId,
    settledSearchQuery,
    technicalDag,
  ]);
  const humanProvenance = useHumanProvenance(
    humanRequest,
    enabled && temporalRequestsEnabled && usesCanonicalSource && !dsmActive,
  );
  useEffect(() => {
    const replay = activeGroundedReplay?.targetView === "human_provenance" ? activeGroundedReplay : null;
    if (!replay) {
      setGroundedHumanDigestState("idle");
      return;
    }
    if (!humanProvenance.data || !humanProvenance.isExactSettled) {
      setGroundedHumanDigestState("checking");
      return;
    }
    let cancelled = false;
    setGroundedHumanDigestState("checking");
    const { page_count: _pageCount, ...sourceResult } = humanProvenance.data;
    void canonicalGroundedSourceDigest(sourceResult).then((actual) => {
      if (!cancelled) {
        setGroundedHumanDigestState(actual === replay.expectedSourceSemanticDigest ? "verified" : "mismatch");
      }
    }).catch(() => {
      if (!cancelled) setGroundedHumanDigestState("mismatch");
    });
    return () => { cancelled = true; };
  }, [activeGroundedReplay, humanProvenance.data, humanProvenance.isExactSettled]);
  const displayedHumanProvenance = activeGroundedReplay?.targetView === "human_provenance"
    ? groundedHumanDigestState === "verified" ? humanProvenance.data : null
    : humanProvenance.data;
  const groundedHumanDigestError = groundedHumanDigestState === "mismatch"
    ? new Error("The reopened human-provenance result does not match the cited source digest.")
    : null;
  const humanFacetRequest = useMemo(() => (
    canonicalCutDisplayable && projectionTemporalCut
      ? buildHumanFacetBaseRequest({
          projectId,
          searchQuery: settledSearchQuery,
          filters: humanFilters,
          cut: projectionTemporalCut,
        })
      : null
  ), [canonicalCutDisplayable, humanFilters, projectId, projectionTemporalCut, settledSearchQuery]);
  const humanFacets = useHumanProvenanceFacets(
    humanFacetRequest,
    enabled && temporalRequestsEnabled && usesCanonicalSource && !dsmActive && humanFilterOpen,
  );
  const journalWriteCapability = useJournalEntryWriteCapability(
    projectId,
    enabled && usesCanonicalSource && !dsmActive && variant !== "compact",
  );
  const humanCompositionReady = canonicalCutDisplayable
    && humanProvenance.isExactSettled
    && !humanProvenance.isPending
    && (activeGroundedReplay?.targetView !== "human_provenance" || groundedHumanDigestState !== "checking")
    && (displayedHumanProvenance !== null || humanProvenance.error !== null || groundedHumanDigestError !== null);
  const composedCanonicalGraph = useMemo(() => {
    if (!technicalDag || !humanCompositionReady) return null;
    const composed = displayedHumanProvenance
      ? composeHumanProvenanceDag(technicalDag, displayedHumanProvenance)
      : suppressV2HumanNodes(technicalDag);
    if (composed === technicalDag && canonicalProjection.graph) return canonicalProjection.graph;
    return normalizeDagData(composed);
  }, [canonicalProjection.graph, displayedHumanProvenance, humanCompositionReady, technicalDag]);
  const canonicalGraphUnavailable = (usesCanonicalSource
    && (!appliedCutIsSettledExact || !humanCompositionReady))
    || compatibilityHistoricalRequested;
  const activeGraph = dsmActive || compatibilityHistoricalRequested
    ? null
    : localGraph ?? composedCanonicalGraph;
  const graphEnvelope = useMemo(() => ({
    graph: activeGraph,
    arrivedAt: performance.now(),
  }), [activeGraph]);
  const graph = graphEnvelope.graph;
  const serverProjectionActive = displayedCanonicalProjection !== null;
  useEffect(() => {
    if (usesCanonicalSource && viewMode === "summary" && summaryGroup !== "category" && summaryGroup !== "state") {
      setSummaryGroup("category");
    }
  }, [summaryGroup, usesCanonicalSource, viewMode]);
  sourceGraphRef.current = graph;
  const categoryResolution = useMemo(() => graph ? resolveDagCategories(graph.nodes) : null, [graph]);
  const categoryKeyById = useMemo(() => {
    const result = new Map<string, string>();
    if (!graph || !categoryResolution) return result;
    graph.nodes.forEach((node, index) => result.set(node.id, categoryResolution.categoryKeyByNode[index]!));
    return result;
  }, [categoryResolution, graph]);
  const categories = useMemo(
    () => orderedCategories(categoryResolution?.categories ?? [], preferences.categoryOrder),
    [categoryResolution, preferences.categoryOrder],
  );
  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const key of categoryResolution?.categoryKeyByNode ?? []) counts.set(key, (counts.get(key) ?? 0) + 1);
    return counts;
  }, [categoryResolution]);
  const facetCounts = useMemo<DagFacetCounts>(() => {
    const counts: DagFacetCounts = {
      recordTypes: new Map<string, number>(),
      pathStates: new Map<string, number>(),
      topology: new Map<string, number>(),
    };
    if (!graph) return counts;
    graph.nodes.forEach((node, index) => {
      counts.recordTypes.set(node.type, (counts.recordTypes.get(node.type) ?? 0) + 1);
      counts.pathStates.set(node.pathState, (counts.pathStates.get(node.pathState) ?? 0) + 1);
      if (graph.incoming[index]!.length === 0) counts.topology.set("root", (counts.topology.get("root") ?? 0) + 1);
      if (graph.outgoing[index]!.length === 0) counts.topology.set("leaf", (counts.topology.get("leaf") ?? 0) + 1);
      if (node.anchor) counts.topology.set("anchor", (counts.topology.get("anchor") ?? 0) + 1);
    });
    return counts;
  }, [graph]);
  const visibleCategoryCount = categories.reduce(
    (count, category) => count + Number(!preferences.hiddenCategoryKeys.includes(category.key)),
    0,
  );
  const categoryOrderKey = categories.map((category) => category.key).join("\u0000");
  const hiddenCategoryKey = preferences.hiddenCategoryKeys.slice().sort().join("\u0000");
  const baseFilterResult = useMemo(
    () => graph
      ? serverProjectionActive
        ? { mask: null, visibleCount: graph.nodes.length, activeGroupCount: 0 }
        : buildDagFilterMask(graph, filters)
      : { mask: null, visibleCount: 0, activeGroupCount: 0 },
    [filters, graph, serverProjectionActive],
  );
  const categoryFilterResult = useMemo(() => {
    if (!graph || !categoryResolution || preferences.hiddenCategoryKeys.length === 0) return baseFilterResult;
    const hidden = new Set(preferences.hiddenCategoryKeys);
    const mask = new Uint8Array(graph.nodes.length);
    let visibleCount = 0;
    for (let index = 0; index < graph.nodes.length; index += 1) {
      const baseVisible = !baseFilterResult.mask || Boolean(baseFilterResult.mask[index]);
      if (!baseVisible || hidden.has(categoryResolution.categoryKeyByNode[index]!)) continue;
      mask[index] = 1;
      visibleCount += 1;
    }
    return { ...baseFilterResult, mask, visibleCount };
  }, [baseFilterResult, categoryResolution, graph, hiddenCategoryKey]);
  const searchDocuments = useMemo(() => graph ? buildDagSearchDocuments(graph) : [], [graph]);
  const canonicalSearchRootOrder = useMemo(() => (
    displayedCanonicalProjection?.projection === "search"
      ? displayedCanonicalProjection.roots.map((root) => `${root.entity_kind}:${root.id}`)
      : []
  ), [displayedCanonicalProjection]);
  const matches = useMemo(
    () => {
      if (!graph) return [];
      if (usesCanonicalSource && searchQuery.trim()) {
        if (settledSearchQuery !== searchQuery || displayedCanonicalProjection?.projection !== "search") return [];
        return canonicalSearchRootOrder
          .map((rootId) => graph.nodeIndexById.get(rootId) ?? -1)
          .filter((index) => index >= 0);
      }
      return findDagMatches(graph, searchDocuments, searchQuery, categoryFilterResult.mask);
    },
    [displayedCanonicalProjection?.projection, canonicalSearchRootOrder, categoryFilterResult.mask, graph, searchDocuments, searchQuery, settledSearchQuery, usesCanonicalSource],
  );
  const summaryBucketFilter = usesCanonicalSource && viewMode === "summary";
  const searchMask = useMemo(
    () => graph && searchQuery.trim() ? indicesToMask(graph.nodes.length, matches) : null,
    [graph, matches, searchQuery],
  );
  const effectiveFilterMask = useMemo(() => {
    if (!searchIsolate || !searchMask || !graph) return categoryFilterResult.mask;
    if (!categoryFilterResult.mask) return searchMask;
    const mask = new Uint8Array(graph.nodes.length);
    for (let index = 0; index < graph.nodes.length; index += 1) {
      mask[index] = categoryFilterResult.mask[index] && searchMask[index] ? 1 : 0;
    }
    return mask;
  }, [categoryFilterResult.mask, graph, searchIsolate, searchMask]);
  const focusProjection = useMemo(() => {
    if (!graph || !selectedId || (viewMode !== "focus" && viewMode !== "impact" && viewMode !== "time_compare")) return null;
    return buildFocusProjection(
      graph,
      selectedId,
      viewMode === "impact" ? "downstream" : direction,
      serverProjectionActive ? null : depth,
      effectiveFilterMask,
    );
  }, [depth, direction, effectiveFilterMask, graph, selectedId, serverProjectionActive, viewMode]);
  const impactArtifactVersionId = useMemo(() => {
    if (viewMode !== "impact" || !serverProjectionActive || !selectedId) return null;
    const selectedRef = parseCanonicalLineageRefKey(selectedId);
    return selectedRef?.entity_kind === "artifact_version" ? selectedRef.id : null;
  }, [selectedId, serverProjectionActive, viewMode]);
  const impactTemporalCut = viewMode === "impact" && serverProjectionActive && appliedCutIsSettledExact
    ? projectionTemporalCut
    : null;
  const impactEffectiveAsOf = impactTemporalCut?.effectiveAsOf ?? null;
  const groundedImpactReplay = activeGroundedReplay?.targetView === "impact" ? activeGroundedReplay : null;
  const impactRequest = groundedImpactReplay
    ? impactRequestFromGrounded(groundedImpactReplay.sourceRequest)
    : impactArtifactVersionId && impactTemporalCut ? {
    project_id: projectId,
    root_ref: { entity_kind: "artifact_version", id: impactArtifactVersionId },
    as_of: impactTemporalCut.effectiveAsOf,
    recorded_as_of: impactTemporalCut.recordedAsOf,
    snapshot_token: impactTemporalCut.snapshotToken,
    max_depth: depth ?? IMPACT_ANALYSIS_DEFAULT_LIMITS.max_depth,
    max_records: IMPACT_ANALYSIS_DEFAULT_LIMITS.max_records,
    max_paths: IMPACT_ANALYSIS_DEFAULT_LIMITS.max_paths,
    max_path_steps: IMPACT_ANALYSIS_DEFAULT_LIMITS.max_path_steps,
  } satisfies ImpactAnalysisRequest : null;
  const impactAnalysis = useImpactAnalysis(impactRequest);
  const groundedImpactDigestError = groundedImpactReplay && impactAnalysis.data
    && impactAnalysis.data.semantic_identity.digest !== groundedImpactReplay.expectedSourceSemanticDigest
    ? new Error("The reopened Impact result does not match the cited source digest.")
    : null;
  const impactPresentation = useMemo(
    () => impactAnalysis.data && !groundedImpactDigestError ? buildImpactPresentation(impactAnalysis.data) : null,
    [groundedImpactDigestError, impactAnalysis.data],
  );
  const impactPanelLoading = usesCanonicalSource
    && viewMode === "impact"
    && Boolean(selectedId)
    && (canonicalProjection.isPending || Boolean(impactRequest && impactAnalysis.isLoading));
  const impactPanelError = !usesCanonicalSource
    ? new Error("Typed impact analysis is unavailable for synthetic or local graph data.")
    : viewMode === "impact" && selectedId && !canonicalProjection.isPending && !impactArtifactVersionId
      ? new Error("Impact requires an exact ArtifactVersion. Select a version record to run typed analysis.")
      : viewMode === "impact" && selectedId && serverProjectionActive && !impactEffectiveAsOf
      ? new Error("The canonical projection did not return an effective snapshot time for impact analysis.")
        : groundedImpactDigestError ?? impactAnalysis.error;
  const compareProjection = useMemo(() => {
    if (!graph || viewMode !== "compare" || !compareA || !compareB) return null;
    return buildCompareProjection(graph, compareA, compareB, serverProjectionActive ? null : depth, effectiveFilterMask);
  }, [compareA, compareB, depth, effectiveFilterMask, graph, serverProjectionActive, viewMode]);
  const summaryProjection = useMemo(() => {
    if (serverProjectionActive || !graph || viewMode !== "summary") return null;
    return buildSummaryProjection(graph, summaryGroup, categoryKeyById, effectiveFilterMask);
  }, [categoryKeyById, effectiveFilterMask, graph, serverProjectionActive, summaryGroup, viewMode]);
  const canvasGraph = useMemo(() => {
    if (!graph) return null;
    if (viewMode === "impact" && focusProjection) {
      const contextGraph = decorateImpactContextGraph(
        decorateFocusGraph(focusProjection, selectedId!),
        selectedId!,
      );
      return impactPresentation ? decorateImpactGraph(contextGraph, impactPresentation) : contextGraph;
    }
    if ((viewMode === "focus" || viewMode === "time_compare") && focusProjection) {
      return decorateFocusGraph(focusProjection, selectedId!);
    }
    if (viewMode === "compare" && compareProjection) return decorateCompareGraph(compareProjection);
    if (viewMode === "summary" && summaryProjection) return summaryProjection.graph;
    return graph;
  }, [compareProjection, focusProjection, graph, impactPresentation, selectedId, summaryProjection, viewMode]);
  graphRef.current = canvasGraph;
  viewModeRef.current = viewMode;
  compareARef.current = compareA;
  compareBRef.current = compareB;
  workspaceStateRef.current = {
    mode: viewMode,
    direction,
    depth,
    compareA,
    compareB,
    timeCompareBeforeInput,
    timeCompareBefore: timeCompareWindow.before,
    timeCompareAfterInput,
    timeCompareAfter: timeCompareWindow.after,
    timeCompareError: timeCompareWindow.error,
    summaryGroup,
    dsmMatrixKind,
    dsmDrillToken,
    dsmCursor,
    dsmNavigationError,
    searchQuery,
    filters,
    asOfInput: appliedEffectiveAsOfInput,
    asOf,
    recordedAsOfInput: appliedRecordedAsOfInput,
    recordedAsOf: appliedTemporalInput.recordedAsOf,
    snapshotTokenInput: appliedSnapshotTokenInput,
    snapshotToken: appliedTemporalInput.snapshotToken,
    temporalError: appliedTemporalInput.error,
  };
  summaryMembersRef.current = summaryProjection?.memberIdsByGroup ?? new Map();
  const activeMatchIndex = activeMatchPosition == null ? null : matches[activeMatchPosition] ?? null;
  const actualVisibleCount = searchIsolate && searchMask ? matches.length : categoryFilterResult.visibleCount;
  const displayFilterMask = useMemo(
    () => graph && canvasGraph
      ? mapSourceMaskToDisplay(graph, canvasGraph, effectiveFilterMask, summaryProjection)
      : null,
    [canvasGraph, effectiveFilterMask, graph, summaryProjection],
  );
  const displaySearchMask = useMemo(
    () => graph && canvasGraph
      ? mapSourceMaskToDisplay(graph, canvasGraph, searchMask, summaryProjection)
      : null,
    [canvasGraph, graph, searchMask, summaryProjection],
  );
  const displayActiveMatchIndex = useMemo(() => {
    if (!graph || !canvasGraph || activeMatchIndex == null) return null;
    const sourceId = graph.nodes[activeMatchIndex]?.id;
    const displayId = sourceId ? displayNodeIdForSource(sourceId, canvasGraph, summaryProjection) : null;
    return displayId ? canvasGraph.nodeIndexById.get(displayId) ?? null : null;
  }, [activeMatchIndex, canvasGraph, graph, summaryProjection]);
  const tableGraph = useMemo(
    () => graph ? projectDagGraphByMask(graph, effectiveFilterMask) : null,
    [effectiveFilterMask, graph],
  );

  useEffect(() => {
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  }, [preferences]);

  const applyWorkspaceUrlState = useCallback((search: string) => {
    const next = parseDagWorkspaceState(search);
    const current = workspaceStateRef.current;
    if (current.mode !== next.mode || current.summaryGroup !== next.summaryGroup) {
      setSelectedSummaryGroupId(null);
    }
    setViewMode(next.mode);
    setDirection(next.direction);
    setDepth(next.depth);
    setCompareA(next.compareA);
    setCompareB(next.compareB);
    setTimeCompareBeforeInput(next.timeCompareBeforeInput);
    setTimeCompareAfterInput(next.timeCompareAfterInput);
    setSummaryGroup(next.summaryGroup);
    setDsmMatrixKind(next.dsmMatrixKind);
    setDsmDrillToken(next.dsmDrillToken);
    setDsmCursor(next.dsmCursor);
    setDsmNavigationError(next.dsmNavigationError);
    dsmHistoryCommitRef.current = null;
    if (next.mode === "dsm") setSearchIsolate(false);
    setSearchQuery(next.searchQuery);
    setFilters({
      ...next.filters,
      recordTypes: [...next.filters.recordTypes],
      pathStates: [...next.filters.pathStates],
      modelClassifications: [...next.filters.modelClassifications],
      topology: [...next.filters.topology],
    });
    setHumanFilters(parseHumanFilterState(search));
    setEffectiveAsOfInput(next.asOfInput);
    setAppliedEffectiveAsOfInput(next.asOfInput);
    setAppliedRecordedAsOfInput(next.recordedAsOfInput);
    setAppliedSnapshotTokenInput(next.snapshotTokenInput);
    temporalHistoryCommitRef.current = next.temporalError === null
      && next.recordedAsOf === null
      && next.snapshotToken === null
      ? "replace"
      : null;
  }, []);

  const urlChangedOutsideWorkspace = observedUrlSearch !== lastObservedUrlSearchRef.current;

  useEffect(() => {
    if (variant === "compact" || typeof window === "undefined") return;
    const syncFromHistory = () => {
      lastObservedUrlSearchRef.current = window.location.search;
      // `writeDagWorkspaceUrl` broadcasts a popstate so router-owned search
      // snapshots stay in sync. That broadcast is not browser navigation and
      // must not re-import a just-superseded URL while a controlled draft is
      // being edited. Real Back/Forward popstates still take this path with
      // the guard clear and restore the complete workspace state.
      if (workspaceUrlWriteInProgressRef.current) return;
      applyWorkspaceUrlState(window.location.search);
    };
    window.addEventListener("popstate", syncFromHistory);
    return () => window.removeEventListener("popstate", syncFromHistory);
  }, [applyWorkspaceUrlState, variant]);

  useEffect(() => {
    if (variant === "compact" || !urlChangedOutsideWorkspace) return;
    lastObservedUrlSearchRef.current = observedUrlSearch;
    applyWorkspaceUrlState(observedUrlSearch);
  }, [applyWorkspaceUrlState, observedUrlSearch, urlChangedOutsideWorkspace, variant]);

  useEffect(() => {
    if (variant === "compact" || urlChangedOutsideWorkspace) return;
    // A temporal URL is committed only after the server has returned all
    // three exact replay fields. Drafts and unbound first-page requests never
    // replace the previously reproducible browser entry.
    if (usesCanonicalSource
      && temporalRequestsEnabled
      && temporalHistoryCommitRef.current !== null
      && !appliedCutIsSettledExact) return;
    if (dsmHistoryCommitRef.current !== null && dsmActive && !lineageDsm.isExactSettled) return;
    const nextWorkspaceState = {
      mode: viewMode,
      direction,
      depth,
      compareA,
      compareB,
      timeCompareBeforeInput,
      timeCompareBefore: timeCompareWindow.before,
      timeCompareAfterInput,
      timeCompareAfter: timeCompareWindow.after,
      timeCompareError: timeCompareWindow.error,
      summaryGroup,
      dsmMatrixKind,
      dsmDrillToken,
      dsmCursor,
      dsmNavigationError,
      searchQuery,
      filters,
      asOfInput: appliedEffectiveAsOfInput,
      asOf,
      recordedAsOfInput: appliedRecordedAsOfInput,
      recordedAsOf: appliedTemporalInput.recordedAsOf,
      snapshotTokenInput: appliedSnapshotTokenInput,
      snapshotToken: appliedTemporalInput.snapshotToken,
      temporalError: appliedTemporalInput.error,
    };
    const temporalHistoryMethod = temporalHistoryCommitRef.current;
    if (usesCanonicalSource && appliedCutIsSettledExact && temporalHistoryMethod !== null) {
      temporalHistoryCommitRef.current = null;
      workspaceUrlWriteInProgressRef.current = true;
      try {
        if (temporalHistoryMethod === "push") pushDagWorkspaceUrl(nextWorkspaceState);
        else updateDagWorkspaceUrl(nextWorkspaceState);
        lastObservedUrlSearchRef.current = window.location.search;
      } finally {
        workspaceUrlWriteInProgressRef.current = false;
      }
      return;
    }
    if (dsmActive && lineageDsm.isExactSettled && dsmHistoryCommitRef.current !== null) {
      dsmHistoryCommitRef.current = null;
      workspaceUrlWriteInProgressRef.current = true;
      try {
        pushDagWorkspaceUrl(nextWorkspaceState);
        lastObservedUrlSearchRef.current = window.location.search;
      } finally {
        workspaceUrlWriteInProgressRef.current = false;
      }
      return;
    }
    workspaceUrlWriteInProgressRef.current = true;
    try {
      updateDagWorkspaceUrl(nextWorkspaceState);
      lastObservedUrlSearchRef.current = window.location.search;
    } finally {
      workspaceUrlWriteInProgressRef.current = false;
    }
  }, [
    appliedCutIsSettledExact,
    appliedEffectiveAsOfInput,
    appliedRecordedAsOfInput,
    appliedSnapshotTokenInput,
    appliedTemporalInput.error,
    appliedTemporalInput.recordedAsOf,
    appliedTemporalInput.snapshotToken,
    asOf,
    compareA,
    compareB,
    depth,
    direction,
    dsmActive,
    dsmCursor,
    dsmDrillToken,
    dsmMatrixKind,
    dsmNavigationError,
    filters,
    lineageDsm.isExactSettled,
    searchQuery,
    summaryGroup,
    timeCompareAfterInput,
    timeCompareBeforeInput,
    timeCompareWindow.after,
    timeCompareWindow.before,
    timeCompareWindow.error,
    temporalRequestsEnabled,
    urlChangedOutsideWorkspace,
    usesCanonicalSource,
    variant,
    viewMode,
  ]);

  useEffect(() => {
    if (!graph) return;
    const resolveId = (value: string | null): string | null => {
      if (!value) return null;
      if (graph.nodeIndexById.has(value)) return value;
      return graph.nodes.find((node) => node.metadata.canonical_id === value)?.id ?? null;
    };
    const resolvedA = resolveId(compareA);
    const resolvedB = resolveId(compareB);
    if (resolvedA !== compareA) setCompareA(resolvedA);
    if (resolvedB !== compareB) setCompareB(resolvedB);
  }, [compareA, compareB, graph]);

  useEffect(() => {
    if (!graph || !selectedId || graph.nodeIndexById.has(selectedId)) return;
    const canonicalMatch = graph.nodes.find((node) => node.metadata.canonical_id === selectedId);
    if (canonicalMatch) setSelectedId(canonicalMatch.id);
  }, [graph, selectedId]);

  useEffect(() => {
    setSelectedId(selectedNodeId);
  }, [selectedNodeId]);

  useEffect(() => {
    if (!canvasGraph) return;
    const displayId = selectedSummaryGroupId
      ?? (selectedId ? displayNodeIdForSource(selectedId, canvasGraph, summaryProjection) : null);
    rendererRef.current?.setSelectedId(displayId);
  }, [canvasGraph, selectedId, selectedSummaryGroupId, summaryProjection]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new DagCanvasRenderer(canvas, {
      onHover: (target: DagHoverTarget | null) => {
        const currentGraph = graphRef.current;
        if (!target || !currentGraph) {
          setHover(null);
          return;
        }
        const maxX = Math.max(12, canvas.clientWidth - 280);
        const maxY = Math.max(12, canvas.clientHeight - 110);
        setHover({
          node: currentGraph.nodes[target.nodeIndex]!,
          x: Math.min(maxX, target.canvasX + 14),
          y: Math.min(maxY, target.canvasY + 14),
        });
      },
      onSelect: (nodeIndex) => {
        const node = nodeIndex == null ? null : graphRef.current?.nodes[nodeIndex] ?? null;
        const nodeId = node?.id ?? null;
        setHover(null);
        if (node?.metadata.interactive === false) return;
        if (node?.metadata.virtual === true) {
          setSelectedSummaryGroupId(nodeId);
          return;
        }
        setSelectedSummaryGroupId(null);
        if (nodeId && viewModeRef.current === "compare") {
          const currentA = compareARef.current;
          const currentB = compareBRef.current;
          if (!currentA || currentB || currentA === nodeId) {
            setCompareA(nodeId);
            setCompareB(null);
          } else {
            setCompareB(nodeId);
          }
        }
        setSelectedId(nodeId);
        selectCallbackRef.current?.(nodeId);
      },
      onActivate: (nodeIndex) => {
        const displayNode = graphRef.current?.nodes[nodeIndex];
        if (displayNode && boundaryActivateRef.current(displayNode)) return;
        if (displayNode?.metadata.projection_bucket) {
          bucketActivateRef.current(displayNode);
          return;
        }
        const virtualMembers = displayNode?.metadata.virtual === true
          ? summaryMembersRef.current.get(displayNode.id) ?? []
          : [];
        const nodeId = virtualMembers[0] ?? displayNode?.id;
        if (nodeId) {
          setHover(null);
          if (displayNode?.metadata.virtual === true) {
            setViewMode("focus");
            setSelectedSummaryGroupId(null);
          }
          setSelectedId(nodeId);
          selectCallbackRef.current?.(nodeId);
          if (displayNode?.metadata.virtual !== true) {
            const canonicalId = displayNode?.metadata.canonical_id;
            const activationId = displayNode?.metadata.canonical_entity_kind === "operation_run" && typeof canonicalId === "string"
              ? canonicalId
              : nodeId;
            activateCallbackRef.current?.(activationId, displayNode);
          }
        }
      },
      onFirstPaint: setFirstPaintMs,
    });
    rendererRef.current = renderer;
    const themeObserver = new MutationObserver(() => renderer.refreshTheme());
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "data-palette"] });
    return () => {
      themeObserver.disconnect();
      renderer.destroy();
      rendererRef.current = null;
    };
  }, []);

  const chronicleCategoryOrderKey = viewMode === "chronicle" ? categoryOrderKey : "";
  const chronicleHiddenCategoryKey = viewMode === "chronicle" ? hiddenCategoryKey : "";

  useEffect(() => {
    if (!canvasGraph || !rendererRef.current || viewMode === "runs" || viewMode === "audit") return;
    const renderer = rendererRef.current;
    const firstGraph = lastLaidOutGraphRef.current == null;
    lastLaidOutGraphRef.current = canvasGraph;
    renderer.setGraph(canvasGraph, firstGraph ? graphEnvelope.arrivedAt : performance.now());
    renderer.setStoryMode(storyMode);
    const selectedDisplayId = selectedSummaryGroupId
      ?? (selectedId ? displayNodeIdForSource(selectedId, canvasGraph, summaryProjection) : null);
    renderer.setSelectedId(selectedDisplayId);
    setFirstPaintMs(null);
    setLayoutDuration(null);
    setLayoutError(null);
    setLayoutProgress(0);
    const client = new DagLayoutWorkerClient();
    client.layout(canvasGraph, {
      onMeta: (meta) => renderer.setLayoutMeta(meta),
      onChunk: (start, positions) => {
        renderer.applyLayoutChunk(start, positions);
        setLayoutProgress((previous) => Math.min(canvasGraph.nodes.length, Math.max(previous, start + positions.length / 2)));
      },
      onComplete: (durationMs) => {
        setLayoutDuration(durationMs);
        setLayoutProgress(canvasGraph.nodes.length);
      },
      onError: setLayoutError,
    }, {
      mode: viewMode === "chronicle"
        ? "chronicle"
        : viewMode === "summary"
          || ((viewMode === "focus" || viewMode === "impact" || viewMode === "time_compare") && focusProjection)
          || (viewMode === "compare" && compareProjection)
          ? "centered"
          : "flow",
      anchorIds: viewMode === "compare"
        ? [compareA, compareB].filter((id): id is string => Boolean(id))
        : selectedId ? [selectedId] : [],
      comparisonRoles: viewMode === "compare" && compareProjection
        ? Object.fromEntries(compareProjection.roleById)
        : undefined,
      categoryOrder: viewMode === "chronicle" ? categories.map((category) => category.key) : undefined,
      hiddenCategoryKeys: viewMode === "chronicle" ? preferences.hiddenCategoryKeys : undefined,
    });
    return () => client.destroy();
  }, [
    canvasGraph,
    chronicleCategoryOrderKey,
    chronicleHiddenCategoryKey,
    graphEnvelope.arrivedAt,
    viewMode,
  ]);

  useEffect(() => rendererRef.current?.setStoryMode(storyMode), [storyMode]);

  useEffect(() => {
    rendererRef.current?.setViewPreferences({
      showCategoryLabels: preferences.showCategoryLabels,
      fontScale: preferences.fontScale,
      importanceByCategory: preferences.importanceByCategory,
    });
  }, [preferences.fontScale, preferences.importanceByCategory, preferences.showCategoryLabels]);

  useEffect(() => {
    rendererRef.current?.setFilterMask(displayFilterMask);
  }, [displayFilterMask]);

  useEffect(() => {
    rendererRef.current?.setSearchMask(displaySearchMask, displayActiveMatchIndex);
  }, [displayActiveMatchIndex, displaySearchMask]);

  useEffect(() => {
    if (activeMatchPosition != null && activeMatchPosition >= matches.length) setActiveMatchPosition(matches.length ? 0 : null);
  }, [activeMatchPosition, matches.length]);

  const selectedIndex = selectedId && graph ? graph.nodeIndexById.get(selectedId) ?? null : null;
  useEffect(() => {
    const replay = activeGroundedReplay;
    const highlight = replay?.highlight;
    if (!replay || !highlight || lastGroundedHighlightIdRef.current === replay.id || !graph) return;
    let nodeId: string | null = null;
    if (highlight.recordKind === "artifact_version") nodeId = `artifact_version:${highlight.recordId}`;
    else if (highlight.recordKind === "decision") nodeId = humanDecisionNodeId(highlight.recordId);
    else if (highlight.recordKind === "journal_entry") nodeId = humanJournalNodeId(highlight.recordId);
    else if (highlight.recordKind === "decision_evidence" && highlight.secondaryRecordId) {
      nodeId = humanEvidenceNodeId(highlight.recordId, highlight.secondaryRecordId);
    }
    if (!nodeId || !graph.nodeIndexById.has(nodeId)) return;
    lastGroundedHighlightIdRef.current = replay.id;
    setSelectedId(nodeId);
    selectCallbackRef.current?.(nodeId);
  }, [activeGroundedReplay, graph]);
  const selectedCanonicalBucket = selectedSummaryGroupId && graph
    ? graph.nodes.find((node) => node.id === selectedSummaryGroupId && node.metadata.projection_bucket) ?? null
    : null;
  const loading = usesCanonicalSource
    && temporalRequestsEnabled
    && (!appliedCutIsSettledExact || !humanCompositionReady);
  const temporalAwaitingApply = temporalDraft.error === null && !temporalDraftMatchesApplied
    ? "Apply the effective/event time to load one exact temporal cut."
    : null;
  const error = temporalDraft.error
    ?? appliedTemporalInput.error
    ?? temporalAwaitingApply
    ?? compatibilityTemporalError
    ?? layoutError
    ?? (usesCanonicalSource
      ? canonicalProjection.error?.message ?? groundedHumanDigestError?.message ?? humanProvenance.error?.message ?? null
      : null);

  const selectNode = (nodeId: string | null) => {
    const requestedNode = nodeId && graph ? graph.nodes[graph.nodeIndexById.get(nodeId) ?? -1] : null;
    if (requestedNode?.metadata.interactive === false) return;
    setHover(null);
    setActiveGroundedReplay(null);
    setOpenedProofContext(null);
    setSelectedSummaryGroupId(null);
    if (nodeId && viewMode === "compare") {
      if (!compareA || compareB || compareA === nodeId) {
        setCompareA(nodeId);
        setCompareB(null);
      } else {
        setCompareB(nodeId);
      }
    }
    setSelectedId(nodeId);
    const displayId = nodeId && canvasGraph
      ? displayNodeIdForSource(nodeId, canvasGraph, summaryProjection)
      : null;
    rendererRef.current?.setSelectedId(displayId);
    selectCallbackRef.current?.(nodeId);
  };
  const activateNode = (nodeId: string) => {
    const node = graph?.nodes[graph.nodeIndexById.get(nodeId) ?? -1];
    if (node && boundaryActivateRef.current(node)) return;
    const canonicalId = node?.metadata.canonical_id;
    const activationId = node?.metadata.canonical_entity_kind === "operation_run" && typeof canonicalId === "string"
      ? canonicalId
      : nodeId;
    activateCallbackRef.current?.(activationId, node);
  };

  const runBenchmark = async () => {
    if (!rendererRef.current) return;
    setBenchmarking(true);
    setBenchmark(await rendererRef.current.runPanZoomBenchmark());
    setBenchmarking(false);
  };

  const moveCategory = (key: string, direction: -1 | 1) => {
    const currentOrder = categories.map((category) => category.key);
    const index = currentOrder.indexOf(key);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= currentOrder.length) return;
    [currentOrder[index], currentOrder[target]] = [currentOrder[target]!, currentOrder[index]!];
    setPreferences((current) => ({ ...current, categoryOrder: currentOrder }));
  };

  const setCategoryVisibility = (key: string, visible: boolean) => {
    setHover(null);
    setPreferences((current) => ({
      ...current,
      hiddenCategoryKeys: visible
        ? current.hiddenCategoryKeys.filter((categoryKey) => categoryKey !== key)
        : [...new Set([...current.hiddenCategoryKeys, key])],
    }));
  };

  const resetPreferences = () => setPreferences({ ...DEFAULT_PREFERENCES });
  const clearDsmNavigation = () => {
    setDsmDrillToken(null);
    setDsmCursor(null);
    setDsmNavigationError(null);
    dsmHistoryCommitRef.current = null;
  };
  const resetFilters = () => {
    clearDsmNavigation();
    setFilters({
      ...DEFAULT_DAG_FILTERS,
      recordTypes: [...DEFAULT_DAG_FILTERS.recordTypes],
      pathStates: [...DEFAULT_DAG_FILTERS.pathStates],
      modelClassifications: [...DEFAULT_DAG_FILTERS.modelClassifications],
      topology: [...DEFAULT_DAG_FILTERS.topology],
    });
    setSearchIsolate(false);
    setActiveMatchPosition(null);
    setHumanFilters({ ...DEFAULT_HUMAN_FILTERS });
    updateHumanFilterUrl(DEFAULT_HUMAN_FILTERS);
  };
  const changeFilters = (next: DagFilterState) => {
    setHover(null);
    clearDsmNavigation();
    if (usesCanonicalSource && isRootedViewMode(viewMode)) setViewMode("chronicle");
    setFilters(withSafeModelClassifications(next));
    setActiveMatchPosition(null);
  };
  const changeHumanFilters = (next: HumanFilterState) => {
    setHover(null);
    setHumanFilters(next);
    updateHumanFilterUrl(next);
    if (selectedId) {
      setSelectedId(null);
      selectCallbackRef.current?.(null);
    }
    if (isRootedViewMode(viewMode)) setViewMode("chronicle");
  };
  const changeEffectiveAsOfInput = (value: string) => {
    setHover(null);
    setOpenedProofContext(null);
    setSelectedSummaryGroupId(null);
    setEffectiveAsOfInput(value);
  };
  const commitEffectiveAsOf = (value: string, forceNewSnapshot: boolean) => {
    const parsed = parseEffectiveAsOfInput(value);
    if (parsed.error !== null) return;
    const normalizedInput = parsed.requestsNow ? "" : parsed.normalized!;
    const sameBoundInstant = !forceNewSnapshot
      && parsed.normalized !== null
      && parsed.normalized === appliedTemporalInput.normalized
      && appliedTemporalInput.recordedAsOf !== null
      && appliedTemporalInput.snapshotToken !== null;
    if (sameBoundInstant) {
      setEffectiveAsOfInput(parsed.normalized!);
      return;
    }
    setHover(null);
    clearDsmNavigation();
    setOpenedProofContext(null);
    setSelectedSummaryGroupId(null);
    setEffectiveAsOfInput(normalizedInput);
    setAppliedEffectiveAsOfInput(normalizedInput);
    // Any new effective/event-time selection must obtain a fresh DB-owned
    // recording cut. Reusing the prior watermark against another instant is
    // forbidden even when that instant is later than recorded/system time.
    setAppliedRecordedAsOfInput(null);
    setAppliedSnapshotTokenInput(null);
    temporalHistoryCommitRef.current = usesCanonicalSource ? "push" : null;
  };
  const applyEffectiveAsOf = () => commitEffectiveAsOf(effectiveAsOfInput, false);
  const useServerNow = () => commitEffectiveAsOf("", true);
  const clearSearch = () => {
    clearDsmNavigation();
    setSearchQuery("");
    setSearchIsolate(false);
    setActiveMatchPosition(null);
    setSearchResultsOpen(false);
    rendererRef.current?.setSearchMask(null, null);
  };
  const goToMatch = (position: number, selectResult = false) => {
    if (!graph || !matches.length || !searchMask) return;
    const nextPosition = (position + matches.length) % matches.length;
    const nodeIndex = matches[nextPosition]!;
    const node = graph.nodes[nodeIndex]!;
    if (selectResult) setSearchResultsOpen(false);
    setActiveMatchPosition(nextPosition);
    const displayId = canvasGraph ? displayNodeIdForSource(node.id, canvasGraph, summaryProjection) : null;
    const displayIndex = displayId && canvasGraph ? canvasGraph.nodeIndexById.get(displayId) ?? null : null;
    rendererRef.current?.setSearchMask(displaySearchMask, displayIndex);
    if (displayId) rendererRef.current?.focusNodeById(displayId);
    if (selectResult && viewMode === "summary" && displayId) {
      setSelectedSummaryGroupId(displayId);
      rendererRef.current?.setSelectedId(displayId);
    } else if (selectResult) {
      selectNode(node.id);
    }
  };
  const stepMatches = (direction: -1 | 1, selectResult = false) => {
    const start = activeMatchPosition == null ? (direction === 1 ? -1 : 0) : activeMatchPosition;
    goToMatch(start + direction, selectResult);
  };
  const onSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && matches.length) {
      event.preventDefault();
      stepMatches(event.shiftKey ? -1 : 1, true);
    } else if (event.key === "ArrowDown" && matches.length) {
      event.preventDefault();
      stepMatches(1);
    } else if (event.key === "ArrowUp" && matches.length) {
      event.preventDefault();
      stepMatches(-1);
    } else if (event.key === "Escape" && searchQuery) {
      event.preventDefault();
      clearSearch();
    }
  };
  const onSurfaceKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === "f" && variant !== "compact") {
      event.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
      setSearchResultsOpen(true);
    }
  };
  const selectedOutsideFilters = selectedIndex != null && effectiveFilterMask != null && !effectiveFilterMask[selectedIndex];
  const resultWindowStart = activeMatchPosition != null && activeMatchPosition > 5
    ? Math.min(activeMatchPosition - 3, Math.max(0, matches.length - 9))
    : 0;
  const visibleResults = matches.slice(resultWindowStart, resultWindowStart + 9);
  const filterChips: Array<{ key: string; label: string; clear: () => void }> = [];
  if (!usesCanonicalSource && filters.recordTypes.length !== ALL_DAG_RECORD_TYPES.length) {
    filterChips.push({
      key: "types",
      label: filters.recordTypes.length ? `Types · ${filters.recordTypes.map((type) => recordTypeLabels[type]).join(", ")}` : "No record types",
      clear: () => changeFilters({ ...filters, recordTypes: [...ALL_DAG_RECORD_TYPES] }),
    });
  }
  const visiblePathStates = usesCanonicalSource
    ? filters.pathStates.filter((state): state is "active" | "invalidated" => state === "active" || state === "invalidated")
    : filters.pathStates;
  const allVisiblePathStatesSelected = usesCanonicalSource
    ? visiblePathStates.length === 2
    : visiblePathStates.length === ALL_DAG_PATH_STATES.length;
  if (!allVisiblePathStatesSelected) {
    filterChips.push({
      key: "states",
      label: visiblePathStates.length
        ? `${usesCanonicalSource ? "Lifecycle" : "State"} · ${visiblePathStates.map((state) => pathStateLabels[state]).join(", ")}`
        : usesCanonicalSource ? "No lifecycle states" : "No path states",
      clear: () => changeFilters({ ...filters, pathStates: [...ALL_DAG_PATH_STATES] }),
    });
  }
  if (usesCanonicalSource && filters.modelClassifications.length !== ALL_MODEL_CLASSIFICATIONS.length) {
    filterChips.push({
      key: "model-classification",
      label: `Model · ${filters.modelClassifications.map((classification) => MODEL_CLASSIFICATION_LABELS[classification]).join(", ")}`,
      clear: () => changeFilters({ ...filters, modelClassifications: [...ALL_MODEL_CLASSIFICATIONS] }),
    });
  }
  if (!usesCanonicalSource && filters.topology.length) {
    filterChips.push({
      key: "topology",
      label: filters.topology.map((facet) => topologyLabels[facet]).join(" · "),
      clear: () => changeFilters({ ...filters, topology: [] }),
    });
  }
  if (filters.timeWindowDays != null) {
    filterChips.push({
      key: "time",
      label: `Latest ${filters.timeWindowDays} days`,
      clear: () => changeFilters({ ...filters, timeWindowDays: null }),
    });
  }
  const humanChipGroups: Array<{
    key: keyof HumanFilterState;
    label: string;
    values: readonly string[];
  }> = [
    { key: "actorIds", label: "Creator / author / status actor", values: humanFilters.actorIds },
    { key: "decisionStatuses", label: "Decision status", values: humanFilters.decisionStatuses },
    { key: "evidenceTypes", label: "Evidence type", values: humanFilters.evidenceTypes },
    { key: "entryTypes", label: "Journal type", values: humanFilters.entryTypes },
    { key: "sensitivityTierIds", label: "Sensitivity", values: humanFilters.sensitivityTierIds },
    { key: "lifecycleStates", label: "Human validity", values: humanFilters.lifecycleStates },
  ];
  if (!dsmActive && humanFilters.recordKinds.length !== ALL_HUMAN_RECORD_KINDS.length) {
    filterChips.push({
      key: "human-recordKinds",
      label: `Human record type · ${humanFilters.recordKinds.join(", ")}`,
      clear: () => changeHumanFilters({
        ...humanFilters,
        recordKinds: [...ALL_HUMAN_RECORD_KINDS],
      }),
    });
  }
  humanChipGroups.forEach((group) => {
    if (dsmActive || !group.values.length) return;
    filterChips.push({
      key: `human-${group.key}`,
      label: `${group.label} · ${group.values.join(", ")}`,
      clear: () => changeHumanFilters({ ...humanFilters, [group.key]: [] }),
    });
  });
  if (!dsmActive && searchIsolate && searchQuery.trim()) {
    filterChips.push({ key: "matches", label: "Search matches only", clear: () => setSearchIsolate(false) });
  }
  const selectedLabel = selectedId && graph ? graph.nodes[graph.nodeIndexById.get(selectedId) ?? -1]?.label ?? null : null;
  const selectedNode = selectedIndex != null && graph ? graph.nodes[selectedIndex] ?? null : null;
  const selectedArtifactVersion = selectedNode ? exactArtifactVersion(selectedNode) : null;
  const selectedReadableArtifactVersion = selectedArtifactVersion?.project_id === projectId
    ? selectedArtifactVersion
    : null;
  const journalContextKey = selectedReadableArtifactVersion && projectionTemporalCut
    ? [
        projectId,
        selectedReadableArtifactVersion.id,
        projectionTemporalCut.effectiveAsOf,
        projectionTemporalCut.recordedAsOf,
        projectionTemporalCut.snapshotToken,
      ].join("\u0000")
    : null;
  const journalWriteDisabledReason = !selectedReadableArtifactVersion
    ? "Select a readable exact ArtifactVersion to publish a journal entry."
    : !appliedCutIsSettledExact || !humanCompositionReady || !projectionTemporalCut
      ? "Wait for the canonical graph and human provenance to settle at one exact cut."
      : journalWriteCapability.error
        ? "Journal publication capability is unavailable for this project."
        : !journalWriteCapability.isExactSettled
          ? "Checking journal publication capability."
          : journalWriteCapability.data?.available !== true
            ? "Journal publication is not available for this project."
            : null;
  const journalFormOpen = journalFormContext !== null
    && journalFormContext === journalContextKey
    && journalWriteDisabledReason === null;
  const journalDecisionOptions = useMemo(
    () => selectedReadableArtifactVersion
      ? visibleJournalDecisionOptions(displayedHumanProvenance, selectedReadableArtifactVersion.id)
      : [],
    [displayedHumanProvenance, selectedReadableArtifactVersion],
  );
  const journalSensitivityTiers = useMemo(
    () => selectedReadableArtifactVersion
      ? visibleJournalSensitivityTiers({
          result: displayedHumanProvenance,
          subjectArtifactVersionId: selectedReadableArtifactVersion.id,
          subjectTier: {
            id: selectedReadableArtifactVersion.sensitivity_tier_id,
            label: selectedReadableArtifactVersion.sensitivity_tier_name,
          },
        })
      : [],
    [displayedHumanProvenance, selectedReadableArtifactVersion],
  );
  useEffect(() => {
    if (journalFormContext !== null && !journalFormOpen) setJournalFormContext(null);
  }, [journalFormContext, journalFormOpen]);
  const proofTemporalCut = dsmActive
    ? lineageDsm.isExactSettled ? lineageDsm.issuedCut : null
    : serverProjectionActive && appliedCutIsSettledExact ? projectionTemporalCut : null;
  const proofArtifactVersionId = dsmActive ? dsmProofRef?.id ?? null : selectedArtifactVersion?.id ?? null;
  const selectedProofContext = proofArtifactVersionId && proofTemporalCut
    ? `${projectId}\u0000${proofArtifactVersionId}\u0000${proofTemporalCut.effectiveAsOf}\u0000${proofTemporalCut.recordedAsOf}\u0000${proofTemporalCut.snapshotToken}`
    : null;
  const groundedProofReplay = activeGroundedReplay?.targetView === "proof_trace" ? activeGroundedReplay : null;
  const proofTraceOpen = groundedProofReplay !== null
    || (selectedProofContext !== null && openedProofContext === selectedProofContext);
  useEffect(() => {
    if (groundedProofReplay) return;
    setOpenedProofContext((current) => current === selectedProofContext ? current : null);
  }, [groundedProofReplay, selectedProofContext]);
  const proofTraceRequest = groundedProofReplay
    ? proofRequestFromGrounded(groundedProofReplay.sourceRequest)
    : proofTraceOpen && proofArtifactVersionId && proofTemporalCut ? {
    project_id: projectId,
    root_ref: { entity_kind: "artifact_version", id: proofArtifactVersionId },
    as_of: proofTemporalCut.effectiveAsOf,
    recorded_as_of: proofTemporalCut.recordedAsOf,
    snapshot_token: proofTemporalCut.snapshotToken,
    max_depth: PROOF_TRACE_DEFAULT_LIMITS.max_depth,
    max_records: PROOF_TRACE_DEFAULT_LIMITS.max_records,
    max_paths: PROOF_TRACE_DEFAULT_LIMITS.max_paths,
    max_path_steps: PROOF_TRACE_DEFAULT_LIMITS.max_path_steps,
  } satisfies ProofTraceRequest : null;
  const timeCompareTemporalCut = viewMode === "time_compare" && serverProjectionActive && appliedCutIsSettledExact
    ? projectionTemporalCut
    : null;
  const timeCompareCutError = timeCompareWindow.error;
  const timeCompareReady = usesCanonicalSource
    && selectedArtifactVersion !== null
    && timeCompareTemporalCut !== null
    && timeCompareWindow.before !== null
    && timeCompareWindow.after !== null
    && timeCompareCutError === null;
  const groundedTemporalReplay = activeGroundedReplay?.targetView === "time_compare" ? activeGroundedReplay : null;
  const temporalCompareRequest = groundedTemporalReplay
    ? temporalRequestFromGrounded(groundedTemporalReplay.sourceRequest)
    : timeCompareReady
    && selectedArtifactVersion
    && timeCompareTemporalCut
    && timeCompareWindow.before
    && timeCompareWindow.after ? {
      project_id: projectId,
      root_refs: [{ entity_kind: "artifact_version", id: selectedArtifactVersion.id }],
      before_as_of: timeCompareWindow.before,
      after_as_of: timeCompareWindow.after,
      recorded_as_of: timeCompareTemporalCut.recordedAsOf,
      snapshot_token: timeCompareTemporalCut.snapshotToken,
      direction,
      max_depth: depth ?? TEMPORAL_COMPARE_DEFAULT_LIMITS.max_depth,
      max_records: TEMPORAL_COMPARE_DEFAULT_LIMITS.max_records,
      max_paths: TEMPORAL_COMPARE_DEFAULT_LIMITS.max_paths,
      max_path_steps: TEMPORAL_COMPARE_DEFAULT_LIMITS.max_path_steps,
    } satisfies TemporalCompareRequest : null;
  useEffect(() => {
    const root = selectedReadableArtifactVersion;
    if (!root || !proofTemporalCut) {
      groundingContextCallbackRef.current?.(null);
      return;
    }
    groundingContextCallbackRef.current?.({
      rootRef: { entityKind: "artifact_version", id: root.id },
      cut: proofTemporalCut,
      comparisonWindow: timeCompareWindow.before && timeCompareWindow.after && timeCompareWindow.error === null
        ? { beforeAsOf: timeCompareWindow.before, afterAsOf: timeCompareWindow.after }
        : null,
    });
  }, [
    proofTemporalCut?.effectiveAsOf,
    proofTemporalCut?.recordedAsOf,
    proofTemporalCut?.snapshotToken,
    selectedReadableArtifactVersion?.id,
    timeCompareWindow.after,
    timeCompareWindow.before,
    timeCompareWindow.error,
  ]);
  const compareALabel = compareA && graph ? graph.nodes[graph.nodeIndexById.get(compareA) ?? -1]?.label ?? compareA : null;
  const compareBLabel = compareB && graph ? graph.nodes[graph.nodeIndexById.get(compareB) ?? -1]?.label ?? compareB : null;
  const canvasMode = viewMode !== "runs" && viewMode !== "audit" && viewMode !== "dsm";
  const modeAnalysisVisible = canvasMode && (
    (viewMode === "impact" && Boolean(selectedId && graph))
    || (viewMode === "time_compare" && Boolean(selectedId && graph))
    || (viewMode === "summary" && Boolean(summaryProjection && selectedSummaryGroupId))
  );
  const analysisPanelVisible = variant !== "compact" && (
    modeAnalysisVisible
    || (canvasMode && viewMode !== "impact" && viewMode !== "summary" && selectedIndex != null)
  );
  const changeViewMode = (nextMode: DagViewMode) => {
    setHover(null);
    setMixerOpen(false);
    setFilterOpen(false);
    setSelectedSummaryGroupId(null);
    setActiveGroundedReplay(null);
    if (nextMode === "dsm") setSearchIsolate(false);
    setViewMode(nextMode);
    if (nextMode === "impact") setDirection("downstream");
  };
  const changeDsmMatrixKind = (nextKind: typeof dsmMatrixKind) => {
    if (nextKind === dsmMatrixKind) return;
    setDsmMatrixKind(nextKind);
    setDsmDrillToken(null);
    setDsmCursor(null);
    setDsmNavigationError(null);
    setDsmProofRef(null);
    dsmHistoryCommitRef.current = "push";
  };
  const drillDsm = (token: string) => {
    setDsmDrillToken(token);
    setDsmCursor(null);
    setDsmNavigationError(null);
    setDsmProofRef(null);
    dsmHistoryCommitRef.current = "push";
  };
  const returnToDsmRoot = () => {
    if (dsmDrillToken === null && dsmCursor === null) return;
    setDsmDrillToken(null);
    setDsmCursor(null);
    setDsmNavigationError(null);
    setDsmProofRef(null);
    dsmHistoryCommitRef.current = "push";
  };
  const continueDsm = (cursor: string) => {
    setDsmCursor(cursor);
    setDsmNavigationError(null);
    setDsmProofRef(null);
    dsmHistoryCommitRef.current = "push";
  };
  const selectDsmArtifactVersion = (selection: LineageDsmArtifactVersionRef) => {
    const nodeId = `artifact_version:${selection.id}`;
    setSelectedId(nodeId);
    selectCallbackRef.current?.(nodeId);
    return nodeId;
  };
  const openDsmView = (selection: LineageDsmArtifactVersionRef, nextMode: "focus" | "impact") => {
    selectDsmArtifactVersion(selection);
    // Rooted canonical projections reject discovery search/filter scope. Keep
    // the exact cut and DSM path for Back, but commit a clean rooted entry.
    setSearchQuery("");
    setSettledSearchQuery("");
    setSearchIsolate(false);
    setFilters({
      ...DEFAULT_DAG_FILTERS,
      recordTypes: [...DEFAULT_DAG_FILTERS.recordTypes],
      pathStates: [...DEFAULT_DAG_FILTERS.pathStates],
      modelClassifications: [...DEFAULT_DAG_FILTERS.modelClassifications],
      topology: [...DEFAULT_DAG_FILTERS.topology],
    });
    setDsmProofRef(null);
    setDsmDrillToken(null);
    setDsmCursor(null);
    setDsmNavigationError(null);
    dsmHistoryCommitRef.current = null;
    setOpenedProofContext(null);
    setViewMode(nextMode);
    temporalHistoryCommitRef.current = "push";
    if (nextMode === "impact") setDirection("downstream");
  };
  const openDsmProof = (selection: LineageDsmArtifactVersionRef) => {
    selectDsmArtifactVersion(selection);
    setDsmProofRef(selection);
    const cut = lineageDsm.isExactSettled ? lineageDsm.issuedCut : null;
    if (cut) setOpenedProofContext(`${projectId}\u0000${selection.id}\u0000${cut.effectiveAsOf}\u0000${cut.recordedAsOf}\u0000${cut.snapshotToken}`);
  };

  useEffect(() => {
    if (!groundedNavigationRequest || lastGroundedNavigationIdRef.current === groundedNavigationRequest.id) return;
    lastGroundedNavigationIdRef.current = groundedNavigationRequest.id;
    if (groundedNavigationRequest.rootRef.entityKind !== "artifact_version") return;

    setActiveGroundedReplay(groundedNavigationRequest);
    const nodeId = `artifact_version:${groundedNavigationRequest.rootRef.id}`;
    const { cut } = groundedNavigationRequest;
    setHover(null);
    clearDsmNavigation();
    setDsmProofRef(null);
    setSearchQuery("");
    setSettledSearchQuery("");
    setSearchIsolate(false);
    setSelectedSummaryGroupId(null);
    setRecordBrowserOpen(false);
    setFilters({
      ...DEFAULT_DAG_FILTERS,
      recordTypes: [...DEFAULT_DAG_FILTERS.recordTypes],
      pathStates: [...DEFAULT_DAG_FILTERS.pathStates],
      modelClassifications: [...DEFAULT_DAG_FILTERS.modelClassifications],
      topology: [...DEFAULT_DAG_FILTERS.topology],
    });
    setSelectedId(nodeId);
    selectCallbackRef.current?.(nodeId);
    setEffectiveAsOfInput(cut.effectiveAsOf);
    setAppliedEffectiveAsOfInput(cut.effectiveAsOf);
    setAppliedRecordedAsOfInput(cut.recordedAsOf);
    setAppliedSnapshotTokenInput(cut.snapshotToken);
    temporalHistoryCommitRef.current = "push";

    if (groundedNavigationRequest.targetView === "proof_trace") {
      setViewMode("focus");
      setOpenedProofContext(`${projectId}\u0000${groundedNavigationRequest.rootRef.id}\u0000${cut.effectiveAsOf}\u0000${cut.recordedAsOf}\u0000${cut.snapshotToken}`);
    } else if (groundedNavigationRequest.targetView === "impact") {
      setOpenedProofContext(null);
      setDirection("downstream");
      setViewMode("impact");
    } else if (groundedNavigationRequest.targetView === "human_provenance") {
      setOpenedProofContext(null);
      setHumanFilters({ ...DEFAULT_HUMAN_FILTERS });
      updateHumanFilterUrl(DEFAULT_HUMAN_FILTERS);
      setViewMode("focus");
    } else {
      setOpenedProofContext(null);
      const comparisonWindow = groundedNavigationRequest.comparisonWindow;
      if (!comparisonWindow) return;
      setTimeCompareBeforeInput(comparisonWindow.beforeAsOf);
      setTimeCompareAfterInput(comparisonWindow.afterAsOf);
      setViewMode("time_compare");
    }
  }, [groundedNavigationRequest, projectId]);

  useEffect(() => {
    if (!commandRequest || lastCommandRequestIdRef.current === commandRequest.id) return;
    lastCommandRequestIdRef.current = commandRequest.id;

    const resolveCategoryKey = (selector: { key?: string; label: string }): string | null => {
      if (selector.key && categories.some((category) => category.key === selector.key)) return selector.key;
      const requested = selector.label.trim().toLocaleLowerCase();
      return categories.find((category) => (
        category.label.toLocaleLowerCase() === requested
        || category.key.toLocaleLowerCase() === requested
        || category.label.toLocaleLowerCase().includes(requested)
      ))?.key ?? null;
    };

    for (const action of commandRequest.actions) {
      switch (action.type) {
        case "set-search":
          clearDsmNavigation();
          if (usesCanonicalSource && isRootedViewMode(viewModeRef.current)) setViewMode("chronicle");
          setSearchQuery(action.query);
          setSearchIsolate(Boolean(action.isolateMatches && action.query.trim()));
          setSearchResultsOpen(Boolean(action.query.trim()));
          setActiveMatchPosition(null);
          break;
        case "set-filter":
          clearDsmNavigation();
          if (usesCanonicalSource && isRootedViewMode(viewModeRef.current)) setViewMode("chronicle");
          setFilters((current) => withSafeModelClassifications({ ...current, ...action.patch }));
          setActiveMatchPosition(null);
          break;
        case "reset-filters":
          resetFilters();
          break;
        case "set-category-labels":
          setPreferences((current) => ({ ...current, showCategoryLabels: action.visible }));
          break;
        case "set-label-scale":
          setPreferences((current) => ({ ...current, fontScale: clampFontScale(action.scale) }));
          break;
        case "adjust-label-scale":
          setPreferences((current) => ({ ...current, fontScale: clampFontScale(current.fontScale + action.delta) }));
          break;
        case "set-category-visibility": {
          const key = resolveCategoryKey(action.category);
          if (key) setCategoryVisibility(key, action.visible);
          break;
        }
        case "show-all-categories":
          setPreferences((current) => ({ ...current, hiddenCategoryKeys: [] }));
          break;
        case "set-category-importance": {
          const key = resolveCategoryKey(action.category);
          if (key) setPreferences((current) => ({
            ...current,
            importanceByCategory: { ...current.importanceByCategory, [key]: action.importance },
          }));
          break;
        }
        case "move-category": {
          const key = resolveCategoryKey(action.category);
          if (!key) break;
          setPreferences((current) => {
            const order = orderedCategories(categoryResolution?.categories ?? [], current.categoryOrder).map((category) => category.key);
            const from = order.indexOf(key);
            if (from < 0) return current;
            const [moved] = order.splice(from, 1);
            const to = action.destination === "first"
              ? 0
              : action.destination === "last"
                ? order.length
                : Math.max(0, Math.min(order.length, from + (action.destination === "up" ? -1 : 1)));
            order.splice(to, 0, moved!);
            return { ...current, categoryOrder: order };
          });
          break;
        }
        case "fit-view":
          window.setTimeout(() => rendererRef.current?.fitToView(), 60);
          break;
        case "run-benchmark":
          window.setTimeout(() => { void runBenchmark(); }, 700);
          break;
        case "reset-viewer":
          clearDsmNavigation();
          setSearchQuery("");
          setSearchIsolate(false);
          setSearchResultsOpen(false);
          setActiveMatchPosition(null);
          setFilters({
            ...DEFAULT_DAG_FILTERS,
            recordTypes: [...DEFAULT_DAG_FILTERS.recordTypes],
            pathStates: [...DEFAULT_DAG_FILTERS.pathStates],
            modelClassifications: [...DEFAULT_DAG_FILTERS.modelClassifications],
            topology: [...DEFAULT_DAG_FILTERS.topology],
          });
          setHumanFilters({ ...DEFAULT_HUMAN_FILTERS });
          updateHumanFilterUrl(DEFAULT_HUMAN_FILTERS);
          useServerNow();
          setPreferences({ ...DEFAULT_PREFERENCES });
          setMixerOpen(false);
          setFilterOpen(false);
          setHumanFilterOpen(false);
          setRecordBrowserOpen(false);
          setStoryMode(false);
          window.setTimeout(() => rendererRef.current?.fitToView(), 60);
          break;
      }
    }
  }, [commandRequest]);

  const canonicalBoundaryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const boundary of displayedCanonicalProjection?.model.context.boundary ?? []) {
      counts.set(boundary.reason, (counts.get(boundary.reason) ?? 0) + 1);
    }
    return counts;
  }, [displayedCanonicalProjection]);
  const canonicalBoundaryCount = [...canonicalBoundaryCounts.values()].reduce((sum, count) => sum + count, 0);
  const canonicalOutsideBoundaryCount = canonicalBoundaryCounts.get("outside_projection") ?? 0;
  const canonicalTemporalHistoryIncomplete = displayedCanonicalProjection?.model.context.temporal_history?.status === "incomplete";

  const legendContent = viewMode === "compare" ? (
    <><span><i className="is-compare-a" />A only</span><span><i className="is-compare-b" />B only</span><span><i className="is-compare-shared" />shared ancestry</span></>
  ) : viewMode === "summary" ? (
    <><span><i className="is-aggregate" />group</span><span>edge width · relationship count</span></>
  ) : viewMode === "impact" ? (
    <>
      <span><i className="is-impact-propagation" />propagation enabled</span>
      <span><i className="is-impact-explicit" />explicit invalidation</span>
      <span><i className="is-impact-stopped" />propagation stopped</span>
      <span><i className="is-impact-indeterminate" />indeterminate</span>
      <span><i className="is-impact-reachable" />reachable context only</span>
    </>
  ) : (
    <>
      <span><i className="is-active" />active path</span>
      <span><i className="is-operation" />operation</span>
      <span><i className="is-decision" />decision</span>
      <span><i className="is-evidence" />evidence</span>
      <span><i className="is-journal" />journal</span>
      <span><i className="is-status" />status history</span>
      <span><i className="is-invalidated" />invalidated</span>
      <span><i className="is-model-generic" />generic model</span>
      <span><i className="is-model-instance" />model instance</span>
      <span><i className="is-model-unclassified" />unclassified</span>
      <span><i className="is-model-context-edge" />instance of · context only</span>
    </>
  );

  return (
    <main
      ref={surfaceRef}
      className={`dag-viewer is-${variant}${streamlined ? " is-streamlined" : ""}`}
      data-testid="dag-viewer-surface"
      style={{ "--dag-ui-font-scale": preferences.fontScale } as CSSProperties}
      onKeyDownCapture={onSurfaceKeyDown}
    >
      <header className="dag-viewer__chrome">
        {streamlined ? toolbarLeading : (
          <div className="dag-viewer__title">
            <span>Lineage · project graph</span>
            <h1>{projectName}</h1>
          </div>
        )}
        {variant !== "compact" && toolbarQuery ? toolbarQuery : variant !== "compact" ? (
          <div
            className="dag-viewer__search"
            onFocus={() => setSearchResultsOpen(true)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setSearchResultsOpen(false);
            }}
          >
            <div className="dag-viewer__search-input">
              <span aria-hidden="true">⌕</span>
              <input
                ref={searchInputRef}
                type="search"
                value={searchQuery}
                maxLength={300}
                placeholder={dsmActive ? "Filter DSM candidates…" : summaryBucketFilter ? "Filter summary buckets…" : "Find in lineage…"}
                aria-label={dsmActive ? "Filter DSM candidates" : summaryBucketFilter ? "Filter summary buckets" : "Find in lineage"}
                onChange={(event) => {
                  clearDsmNavigation();
                  if (usesCanonicalSource && isRootedViewMode(viewMode)) setViewMode("chronicle");
                  setSearchQuery(event.target.value);
                  setActiveMatchPosition(null);
                  if (!event.target.value.trim()) setSearchIsolate(false);
                  setSearchResultsOpen(true);
                }}
                onKeyDown={onSearchKeyDown}
              />
              {searchQuery.trim() ? (
                dsmActive ? <>
                  <output aria-live="polite">server scope</output>
                  <button type="button" onClick={clearSearch} aria-label="Clear DSM candidate filter">×</button>
                </> : summaryBucketFilter ? <>
                  <output aria-live="polite">filtered</output>
                  <button type="button" onClick={clearSearch} aria-label="Clear summary bucket filter">×</button>
                </> : <>
                  <output aria-live="polite">{matches.length ? `${(activeMatchPosition ?? 0) + 1}/${matches.length}` : "0"}</output>
                  <button type="button" onClick={() => stepMatches(-1)} disabled={!matches.length} aria-label="Previous lineage match">↑</button>
                  <button type="button" onClick={() => stepMatches(1)} disabled={!matches.length} aria-label="Next lineage match">↓</button>
                  <button type="button" onClick={clearSearch} aria-label="Clear lineage search">×</button>
                </>
              ) : <kbd>⌘F</kbd>}
            </div>
            {searchResultsOpen && searchQuery.trim() && !summaryBucketFilter && !dsmActive ? (
              <div className="dag-viewer__search-results" role="listbox" aria-label="Lineage search results" data-testid="dag-viewer-search-results">
                <header>
                  <span>{integerFormatter.format(matches.length)} {matches.length === 1 ? "match" : "matches"}</span>
                  <div>
                    <button
                      type="button"
                      className={searchIsolate ? "is-active" : ""}
                      aria-pressed={searchIsolate}
                      disabled={!matches.length}
                      onClick={() => setSearchIsolate((value) => !value)}
                    >{searchIsolate ? "Showing only matches" : "Show only matches"}</button>
                    <button type="button" disabled={!matches.length || !displaySearchMask} onClick={() => displaySearchMask && rendererRef.current?.fitToMask(displaySearchMask)}>Fit matches</button>
                  </div>
                </header>
                {matches.length ? (
                  <div className="dag-viewer__search-result-list">
                    {visibleResults.map((nodeIndex, offset) => {
                      const node = graph!.nodes[nodeIndex]!;
                      const position = resultWindowStart + offset;
                      return (
                        <button
                          type="button"
                          role="option"
                          aria-selected={position === activeMatchPosition}
                          className={position === activeMatchPosition ? "is-active" : ""}
                          key={node.id}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => goToMatch(position, true)}
                        >
                          <i className={`is-${node.pathState}`} aria-hidden="true" />
                          <span><strong>{node.label}</strong><small>{node.kind} · {formatTimestamp(node.timestamp)}</small></span>
                          <code>{shortDigest(node.digest ?? node.id)}</code>
                        </button>
                      );
                    })}
                  </div>
                ) : <p>No records match this search and the active filters.</p>}
                <footer>Global search supports <code>name:&quot;lift plot&quot;</code>, <code>kind:mesh</code>, <code>state:invalidated</code>, and <code>after:2026-07-01</code>.</footer>
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="dag-viewer__counts" data-testid="dag-viewer-counts">
          {dsmActive ? lineageDsm.result ? (
            <><b>{integerFormatter.format(lineageDsm.result.bands.length)}</b> bands · <b>{integerFormatter.format(lineageDsm.result.cells.length)}</b> non-zero cells</>
          ) : "loading bounded matrix…" : graph && streamlined ? (
            (effectiveFilterMask || searchIsolate)
              ? <><b>{integerFormatter.format(actualVisibleCount)}</b> / {integerFormatter.format(graph.nodes.length)}</>
              : <><b>{integerFormatter.format(graph.nodes.length)}</b> records</>
          ) : graph && variant === "compact" ? (
            <><b>{integerFormatter.format(graph.nodes.length)}</b> records</>
          ) : graph ? (
            <>
              {(effectiveFilterMask || searchIsolate) ? <><b>{integerFormatter.format(actualVisibleCount)}</b> of </> : null}
              <b>{integerFormatter.format(graph.nodes.length)}</b> records · <b>{integerFormatter.format(graph.edges.length)}</b> typed edges
            </>
          ) : "loading records…"}
          {syntheticData ? <em>{integerFormatter.format(syntheticNodeCount ?? 0)}-node fixture</em> : null}
        </div>
        <div className="dag-viewer__actions" data-tour="dag-controls">
          {streamlined && variant !== "compact" ? (
            <label className="dag-viewer__view-select">
              <span>View</span>
              <select
                aria-label="Lineage view"
                value={viewMode}
                onChange={(event) => changeViewMode(event.target.value as DagViewMode)}
              >
                {DAG_VIEW_DEFINITIONS.map((view) => (
                  <option key={view.id} value={view.id}>{view.label}</option>
                ))}
              </select>
            </label>
          ) : null}
          {canvasMode ? (
            <>
              <button type="button" onClick={() => rendererRef.current?.zoomBy(0.8)} aria-label="Zoom out">−</button>
              <button type="button" onClick={() => displayFilterMask ? rendererRef.current?.fitToMask(displayFilterMask) : rendererRef.current?.fitToView()}>Fit</button>
              <button type="button" onClick={() => rendererRef.current?.zoomBy(1.25)} aria-label="Zoom in">+</button>
            </>
          ) : null}
          {variant !== "compact" ? (
            <>
              {usesCanonicalSource && !dsmActive ? (
                <button
                  type="button"
                  data-testid="journal-entry-open"
                  disabled={journalWriteDisabledReason !== null}
                  title={journalWriteDisabledReason ?? "Publish immutable human journal entry"}
                  aria-label={journalWriteDisabledReason
                    ? `Journal entry unavailable: ${journalWriteDisabledReason}`
                    : "Publish journal entry for selected ArtifactVersion"}
                  aria-expanded={journalFormOpen}
                  onClick={() => {
                    if (!journalContextKey || journalWriteDisabledReason !== null) return;
                    setJournalWriteNotice(null);
                    setJournalFormContext(journalContextKey);
                    setFilterOpen(false);
                    setHumanFilterOpen(false);
                    setMixerOpen(false);
                  }}
                >Journal entry</button>
              ) : null}
              <button
                type="button"
                className={filterOpen ? "is-active" : ""}
                aria-expanded={filterOpen}
                onClick={() => {
                  setFilterOpen((open) => !open);
                  setMixerOpen(false);
                  setHumanFilterOpen(false);
                }}
                data-testid="dag-viewer-filter-toggle"
              >
                Filter {baseFilterResult.activeGroupCount ? <span>{baseFilterResult.activeGroupCount}</span> : null}
              </button>
              {usesCanonicalSource && !dsmActive ? (
                <button
                  type="button"
                  className={humanFilterOpen ? "is-active" : ""}
                  aria-expanded={humanFilterOpen}
                  onClick={() => {
                    setHumanFilterOpen((open) => !open);
                    setFilterOpen(false);
                    setMixerOpen(false);
                  }}
                  data-testid="human-provenance-filter-toggle"
                >Human {humanFilterCount(humanFilters) ? <span>{humanFilterCount(humanFilters)}</span> : null}</button>
              ) : null}
              {!dsmActive ? <button
                type="button"
                className={mixerOpen ? "is-active" : ""}
                aria-expanded={mixerOpen}
                onClick={() => {
                  setMixerOpen((open) => !open);
                  setFilterOpen(false);
                  setHumanFilterOpen(false);
                }}
                data-testid="dag-viewer-category-toggle"
              >
                {streamlined ? "Display" : "Categories"} <span>{visibleCategoryCount}/{categories.length}</span>
              </button> : null}
              {!streamlined ? (
                <>
                  {!dsmActive ? <button type="button" className={recordBrowserOpen ? "is-active" : ""} aria-expanded={recordBrowserOpen} onClick={() => setRecordBrowserOpen((open) => !open)}>Records</button> : null}
                  {canvasMode ? (
                    <button
                      type="button"
                      className={storyMode ? "is-active" : ""}
                      aria-pressed={storyMode}
                      onClick={() => {
                        setHover(null);
                        setStoryMode((value) => !value);
                      }}
                      data-testid="dag-viewer-story-toggle"
                    >
                      {storyMode ? "All records" : "Story paths"}
                    </button>
                  ) : null}
                </>
              ) : (
                <details className="dag-viewer__more">
                  <summary aria-label="More lineage actions">•••</summary>
                  <div>
                    <button
                      type="button"
                      className={`is-narrow-only${filterOpen ? " is-active" : ""}`}
                      onClick={() => {
                        setFilterOpen((open) => !open);
                        setMixerOpen(false);
                        setHumanFilterOpen(false);
                      }}
                    >Filter {baseFilterResult.activeGroupCount ? `· ${baseFilterResult.activeGroupCount}` : ""}</button>
                    {usesCanonicalSource && !dsmActive ? (
                      <button
                        type="button"
                        className={humanFilterOpen ? "is-active" : ""}
                        onClick={() => {
                          setHumanFilterOpen((open) => !open);
                          setFilterOpen(false);
                          setMixerOpen(false);
                        }}
                      >Human filters {humanFilterCount(humanFilters) ? `· ${humanFilterCount(humanFilters)}` : ""}</button>
                    ) : null}
                    {!dsmActive ? <button
                      type="button"
                      className={`is-narrow-only${mixerOpen ? " is-active" : ""}`}
                      onClick={() => {
                        setMixerOpen((open) => !open);
                        setFilterOpen(false);
                        setHumanFilterOpen(false);
                      }}
                    >Display · {visibleCategoryCount}/{categories.length}</button> : null}
                    {!dsmActive ? <button type="button" className={recordBrowserOpen ? "is-active" : ""} onClick={() => setRecordBrowserOpen((open) => !open)}>Record browser</button> : null}
                    {canvasMode ? (
                      <button
                        type="button"
                        className={storyMode ? "is-active" : ""}
                        onClick={() => {
                          setHover(null);
                          setStoryMode((value) => !value);
                        }}
                      >{storyMode ? "Show all records" : "Show story paths"}</button>
                    ) : null}
                    {syntheticData && canvasMode ? <button type="button" onClick={runBenchmark} disabled={benchmarking}>{benchmarking ? "Measuring…" : "Run benchmark"}</button> : null}
                    <button type="button" onClick={resetPreferences}>Reset display</button>
                  </div>
                </details>
              )}
            </>
          ) : null}
          {onExpand ? <button type="button" onClick={onExpand} aria-label="Expand lineage graph">Expand ↗</button> : null}
          {!streamlined && syntheticData && canvasMode ? <button type="button" onClick={runBenchmark} disabled={benchmarking}>{benchmarking ? "Measuring…" : "Perf run"}</button> : null}
        </div>
      </header>
      {variant !== "compact" ? (
        <TemporalLineageControl
          effectiveInput={effectiveAsOfInput}
          normalizedEffectiveAsOf={temporalDraft.normalized}
          validationError={temporalDraft.error ?? appliedTemporalInput.error ?? compatibilityTemporalError}
          cut={usesCanonicalSource && appliedCutIsSettledExact ? issuedTemporalCut : null}
          isFreezing={usesCanonicalSource && temporalRequestsEnabled && !appliedCutIsSettledExact}
          supportsTemporalReplay={usesCanonicalSource}
          onEffectiveInput={changeEffectiveAsOfInput}
          onApply={applyEffectiveAsOf}
          onUseNow={useServerNow}
        />
      ) : null}
      {journalWriteNotice ? (
        <div className="human-provenance__journal-notice" role="status" data-testid="journal-entry-write-notice">
          <span>{journalWriteNotice}</span>
          <button type="button" onClick={() => setJournalWriteNotice(null)} aria-label="Dismiss journal publication status">×</button>
        </div>
      ) : null}
      {displayedCanonicalProjection ? (
        <div className="dag-viewer__projection-status" data-testid="dag-projection-status" role="status">
          <div>
            <span>{displayedCanonicalProjection.projection}</span>
            {displayedCanonicalProjection.buckets.length ? (
              <strong>
                {integerFormatter.format(displayedCanonicalProjection.buckets.length)} groups represent {integerFormatter.format(displayedCanonicalProjection.representedRecordCount)} records
              </strong>
            ) : (
              <strong>{integerFormatter.format(displayedCanonicalProjection.loadedRecordCount)} canonical records loaded</strong>
            )}
            {canonicalTemporalHistoryIncomplete ? (
              <small data-testid="dag-temporal-history-incomplete">Recorded history incomplete · conclusions are limited to visible evidence</small>
            ) : null}
            {canonicalBoundaryCount ? <small>{integerFormatter.format(canonicalBoundaryCount)} explicit boundaries</small> : null}
            {displayedHumanProvenance ? (
              <small data-testid="human-provenance-status">
                {integerFormatter.format(displayedHumanProvenance.items.length)} human.v1 records · decisions, evidence, and journals remain distinct
              </small>
            ) : null}
            {activeGroundedReplay?.targetView === "human_provenance"
              && groundedHumanDigestState === "verified"
              && activeGroundedReplay.highlight?.sourceLocator === "/items"
              ? <small data-testid="grounded-human-page-highlight" role="status">Opened the cited authorized human-provenance page.</small>
              : null}
            {displayedHumanProvenance?.temporal_history.status === "incomplete" ? <small>Human status history is incomplete at this cut</small> : null}
            {displayedHumanProvenance?.detail?.entity_kind === "decision"
              && displayedHumanProvenance.detail.evidence.disclosure === "partial"
              ? <small data-testid="human-provenance-partial-status">Evidence chain is partially visible · restricted facts are not counted</small>
              : null}
            {displayedCanonicalProjection.terminalTruncation ? <small>Bounded result · no further page is available</small> : null}
            {canonicalProjection.retentionLimitReached ? <small>Browser safety limit reached · search, filter, or focus to narrow the projection</small> : null}
            {humanProvenance.retentionLimitReached ? <small>Human provenance safety limit reached · search, filter, or focus to narrow the projection</small> : null}
            {!canonicalTemporalHistoryIncomplete
              && !displayedCanonicalProjection.truncated
              && !canonicalProjection.hasNextPage
              && !canonicalBoundaryCount
              && displayedHumanProvenance?.truncated !== true
              && !humanProvenance.hasNextPage
              ? <small>Complete result</small> : null}
          </div>
          {canonicalProjection.hasNextPage ? (
            <button
              type="button"
              disabled={canonicalProjection.isFetchingNextPage}
              onClick={() => { void canonicalProjection.fetchNextPage(); }}
            >{canonicalProjection.isFetchingNextPage ? "Loading…" : "Load more"}</button>
          ) : null}
          {humanProvenance.hasNextPage ? (
            <button
              type="button"
              disabled={humanProvenance.isFetchingNextPage}
              onClick={() => { void humanProvenance.fetchNextPage(); }}
            >{humanProvenance.isFetchingNextPage ? "Loading human provenance…" : "Load more human provenance"}</button>
          ) : null}
        </div>
      ) : null}
      {variant !== "compact" ? (
        <>
          {!streamlined ? <DagViewSwitcher mode={viewMode} onChange={changeViewMode} /> : null}
          {!streamlined || viewMode === "focus" || viewMode === "impact" || viewMode === "compare" || viewMode === "time_compare" || viewMode === "summary" || viewMode === "dsm" ? <DagContextBar
            mode={viewMode}
            selectedLabel={selectedLabel}
            direction={direction}
            depth={depth}
            compareALabel={compareALabel}
            compareBLabel={compareBLabel}
            timeCompareBeforeInput={timeCompareBeforeInput}
            timeCompareAfterInput={timeCompareAfterInput}
            timeCompareError={timeCompareCutError}
            timeCompareReady={timeCompareReady}
            timeCompareRecordedAsOf={timeCompareTemporalCut?.recordedAsOf ?? null}
            timeCompareSnapshotToken={timeCompareTemporalCut?.snapshotToken ?? null}
            summaryGroup={summaryGroup}
            serverBacked={usesCanonicalSource}
            boundaryCount={usesCanonicalSource ? canonicalOutsideBoundaryCount : focusProjection?.boundaryCount ?? 0}
            resultCount={focusProjection ? Math.max(0, focusProjection.graph.nodes.length - Number(viewMode === "impact")) : undefined}
            impactSummary={impactPresentation?.summary ?? null}
            impactEffectiveAsOf={impactPresentation?.effectiveAsOf ?? impactEffectiveAsOf}
            impactComplete={impactPresentation?.complete}
            onDirection={setDirection}
            onDepth={setDepth}
            onSummaryGroup={(groupBy) => {
              setSelectedSummaryGroupId(null);
              setSummaryGroup(groupBy);
            }}
            onSwapCompare={() => {
              setCompareA(compareB);
              setCompareB(compareA);
            }}
            onClearCompare={() => {
              setCompareA(null);
              setCompareB(null);
            }}
            onTimeCompareBefore={setTimeCompareBeforeInput}
            onTimeCompareAfter={setTimeCompareAfterInput}
          /> : null}
        </>
      ) : null}
      {variant !== "compact" && filterChips.length ? (
        <div className="dag-viewer__filter-chips" aria-label="Active lineage filters">
          <span>{dsmActive
            ? <>Applied before aggregation · <b>{integerFormatter.format(lineageDsm.result?.bands.length ?? 0)}</b> returned bands</>
            : <>Showing <b>{integerFormatter.format(actualVisibleCount)}</b> of {integerFormatter.format(graph?.nodes.length ?? 0)}</>}</span>
          {filterChips.map((chip) => <button type="button" key={chip.key} onClick={chip.clear}>{chip.label} <i aria-hidden="true">×</i></button>)}
          <button type="button" className="is-clear" onClick={resetFilters}>Clear all</button>
        </div>
      ) : null}
      <div className={`dag-viewer__stage${analysisPanelVisible ? " has-selection" : ""}${modeAnalysisVisible ? " has-analysis" : ""}${proofTraceRequest ? " has-proof-trace" : ""}`}>
        <section
          className={`dag-viewer__viewport${!canvasMode || recordBrowserOpen || canonicalGraphUnavailable ? " is-hidden" : ""}`}
          aria-label="Project lineage graph"
          aria-hidden={!canvasMode || recordBrowserOpen || Boolean(proofTraceRequest) || canonicalGraphUnavailable}
        >
        <canvas
          ref={canvasRef}
          hidden={canonicalGraphUnavailable}
          aria-hidden={canonicalGraphUnavailable}
          tabIndex={!canvasMode || recordBrowserOpen || proofTraceRequest || canonicalGraphUnavailable ? -1 : 0}
          aria-label="Interactive lineage graph. Drag to pan, scroll to zoom, click a record for details, and double-click to open."
        />
        {hover ? <NodeTooltip hover={hover} /> : null}
        {loading ? <div className="dag-viewer__status" role="status">Loading project graph…</div> : null}
        {!loading && graph?.nodes.length === 0 ? <div className="dag-viewer__status">No lineage records in this project.</div> : null}
        {error ? <div className="dag-viewer__status is-error" role="alert">{error}</div> : null}
        {graph && categories.length > 0 && visibleCategoryCount === 0 ? (
          <div className="dag-viewer__status">All categories are hidden. Open Categories to bring records back.</div>
        ) : null}
        {graph && graph.nodes.length > 0 && visibleCategoryCount > 0 && actualVisibleCount === 0 ? (
          <div className="dag-viewer__status">No records match the active filters. Open Filter or clear the filter chips.</div>
        ) : null}
        {canvasGraph && !error && layoutProgress < canvasGraph.nodes.length ? (
          <div className="dag-viewer__stream" role="status">laying out {integerFormatter.format(layoutProgress)} / {integerFormatter.format(canvasGraph.nodes.length)}</div>
        ) : null}
        {streamlined ? (
          <details className="dag-viewer__legend is-collapsible">
            <summary>Legend</summary>
            <div>{legendContent}</div>
          </details>
        ) : <div className="dag-viewer__legend" aria-label="Graph legend">{legendContent}</div>}
        {(firstPaintMs != null || layoutDuration != null || benchmark) ? (
          <output className="dag-viewer__metrics" data-testid="dag-viewer-metrics">
            {firstPaintMs != null ? <span>paint <b>{firstPaintMs.toFixed(0)} ms</b></span> : null}
            {layoutDuration != null ? <span>worker <b>{layoutDuration.toFixed(0)} ms</b></span> : null}
            {benchmark ? <span>pan/zoom <b>{benchmark.fps.toFixed(0)} fps</b> · p95 {benchmark.p95FrameMs.toFixed(1)} ms</span> : null}
          </output>
        ) : null}
        {((viewMode === "focus" || viewMode === "impact" || viewMode === "time_compare") && !selectedId) || (viewMode === "compare" && (!compareA || !compareB)) ? (
          <div className="dag-viewer__empty-prompt">
            <strong>{viewMode === "compare" ? "Choose two records for ancestry compare" : viewMode === "time_compare" ? "Choose one ArtifactVersion for Time Compare" : "Choose an anchor record"}</strong>
            <span>Click a record on the graph or use lineage search. The full graph remains available while you choose.</span>
          </div>
        ) : null}
        </section>
        {proofTraceRequest ? (
          <ProofTracePanel
            request={proofTraceRequest}
            expectedSemanticDigest={groundedProofReplay?.expectedSourceSemanticDigest ?? null}
            initialSourceLocator={groundedProofReplay?.highlight?.sourceLocator ?? null}
            onClose={() => {
              setOpenedProofContext(null);
              setDsmProofRef(null);
              if (activeGroundedReplay?.targetView === "proof_trace") setActiveGroundedReplay(null);
            }}
            onReturnFocus={() => surfaceRef.current?.querySelector<HTMLButtonElement>(dsmActive ? ".lineage-dsm__band-actions button" : "[data-testid='dag-viewer-proof-trace']")?.focus()}
          />
        ) : dsmActive ? (
          <LineageDsmMatrix
            result={lineageDsm.result}
            matrixKind={dsmMatrixKind}
            isLoading={usesCanonicalSource && dsmNavigationError === null && (lineageDsm.isPending || lineageDsm.isFetching || lineageDsm.result === null)}
            error={usesCanonicalSource ? lineageDsm.error : new Error("DSM requires canonical server lineage; local graph data is never aggregated as a fallback.")}
            navigationError={dsmNavigationError}
            onMatrixKind={changeDsmMatrixKind}
            onDrill={drillDsm}
            onRoot={returnToDsmRoot}
            onNextPage={continueDsm}
            onOpenFocus={(selection) => openDsmView(selection, "focus")}
            onOpenProof={openDsmProof}
            onOpenImpact={(selection) => openDsmView(selection, "impact")}
          />
        ) : recordBrowserOpen && graph ? (
          <AccessibleRecordBrowser
            graph={graph}
            filterMask={effectiveFilterMask}
            selectedId={selectedId}
            onClose={() => setRecordBrowserOpen(false)}
            onSelect={selectNode}
            onActivate={activateNode}
            onOpenProof={selectedProofContext ? () => setOpenedProofContext(selectedProofContext) : undefined}
            humanResult={displayedHumanProvenance}
          />
        ) : !canvasMode && tableGraph ? (
          viewMode === "runs" ? (
            <RunMatrixView graph={tableGraph} selectedId={selectedId} onSelect={selectNode} onActivate={activateNode} />
          ) : (
            <AuditView graph={tableGraph} selectedId={selectedId} onSelect={selectNode} onActivate={activateNode} />
          )
        ) : !canvasMode ? (
          <div className="dag-viewer__status" role="status">Loading project graph…</div>
        ) : null}
        {mixerOpen ? (
          <CategoryMixer
            categories={categories}
            categoryCounts={categoryCounts}
            preferences={preferences}
            onClose={() => setMixerOpen(false)}
            onFontScale={(fontScale) => setPreferences((current) => ({ ...current, fontScale: clampFontScale(fontScale) }))}
            onImportance={(key, importance) => setPreferences((current) => ({
              ...current,
              importanceByCategory: { ...current.importanceByCategory, [key]: importance },
            }))}
            onLabels={(showCategoryLabels) => setPreferences((current) => ({ ...current, showCategoryLabels }))}
            onMove={moveCategory}
            onReset={resetPreferences}
            onShowAll={() => setPreferences((current) => ({ ...current, hiddenCategoryKeys: [] }))}
            onVisibility={setCategoryVisibility}
          />
        ) : null}
        {filterOpen ? (
          <FilterPanel
            counts={facetCounts}
            filters={filters}
            serverBacked={usesCanonicalSource}
            onChange={changeFilters}
            onClose={() => setFilterOpen(false)}
            onReset={resetFilters}
          />
        ) : null}
        {humanFilterOpen && projectionTemporalCut ? (
          <HumanFacetFilters
            filters={humanFilters}
            buckets={humanFacets.buckets}
            effectiveAsOf={projectionTemporalCut.effectiveAsOf}
            isLoading={humanFacets.isFetching}
            retentionLimitReached={humanFacets.retentionLimitReached}
            error={humanFacets.error}
            onChange={changeHumanFilters}
            onClose={() => setHumanFilterOpen(false)}
          />
        ) : null}
        {journalFormOpen
          && journalWriteCapability.data
          && projectionTemporalCut
          && selectedReadableArtifactVersion
          && selectedNode ? (
          <JournalEntryForm
            projectId={projectId}
            subject={{
              artifactVersionId: selectedReadableArtifactVersion.id,
              label: selectedNode.label,
              sensitivityTierId: selectedReadableArtifactVersion.sensitivity_tier_id,
              sensitivityTierName: selectedReadableArtifactVersion.sensitivity_tier_name,
            }}
            cut={projectionTemporalCut}
            capability={journalWriteCapability.data}
            decisions={journalDecisionOptions}
            sensitivityTiers={journalSensitivityTiers}
            onClose={() => setJournalFormContext(null)}
            onPublished={(result) => {
              setJournalFormContext(null);
              setJournalWriteNotice(result.replayed
                ? `Journal entry ${result.id} was already published. Refreshing at a fresh exact cut.`
                : `Journal entry ${result.id} was published. Refreshing at a fresh exact cut.`);
              // The prior recorded watermark cannot contain this command.
              // Preserve the visible validity-time instant while omitting the
              // old recording watermark so both projections freeze a fresh,
              // mutually exact trio.
              commitEffectiveAsOf(projectionTemporalCut.effectiveAsOf, true);
            }}
          />
        ) : null}
        {!proofTraceRequest && !recordBrowserOpen && selectedCanonicalBucket ? (
          <aside className="dag-viewer__detail dag-viewer__bucket-detail" data-testid="dag-bucket-detail" aria-label="Lineage group details">
            <header>
              <div><span>Server projection group</span><h2>{selectedCanonicalBucket.label}</h2></div>
              <button type="button" onClick={() => setSelectedSummaryGroupId(null)} aria-label="Close group details">×</button>
            </header>
            <dl>
              <div><dt>Records</dt><dd>{integerFormatter.format(Number(selectedCanonicalBucket.metadata.member_count) || 0)}</dd></div>
              <div><dt>Dimension</dt><dd>{String((selectedCanonicalBucket.metadata.projection_bucket as { dimension?: unknown }).dimension ?? "group")}</dd></div>
            </dl>
            <button className="dag-viewer__open-record" type="button" onClick={() => bucketActivateRef.current(selectedCanonicalBucket)}>
              Explore this group
            </button>
          </aside>
        ) : !proofTraceRequest && !recordBrowserOpen && viewMode === "impact" && graph && selectedId ? (
          <ImpactResultsPanel
            sourceGraph={focusProjection?.analysisGraph ?? graph}
            presentation={impactPresentation}
            anchorId={selectedId}
            isLoading={impactPanelLoading}
            error={impactPanelError}
            onRetry={impactRequest ? impactAnalysis.refresh : undefined}
            onSelect={selectNode}
            onActivate={activateNode}
          />
        ) : !proofTraceRequest && !recordBrowserOpen && viewMode === "time_compare" && temporalCompareRequest ? (
          <TemporalComparePanel
            request={temporalCompareRequest}
            expectedSemanticDigest={groundedTemporalReplay?.expectedSourceSemanticDigest ?? null}
            initialSourceLocator={groundedTemporalReplay?.highlight?.sourceLocator ?? null}
            onClose={() => { setActiveGroundedReplay(null); selectNode(null); }}
            onReturnFocus={() => surfaceRef.current?.querySelector<HTMLButtonElement>("[data-testid='dag-view-mode-time_compare']")?.focus()}
          />
        ) : !proofTraceRequest && !recordBrowserOpen && viewMode === "time_compare" && graph && selectedId ? (
          <aside
            className="dag-analysis-panel dag-time-compare"
            aria-label="Time Compare changes"
            data-time-compare-ready={timeCompareReady ? "true" : "false"}
          >
            <header className="dag-analysis-panel__header">
              <div><span className="dag-analysis-panel__eyebrow">Temporal evidence</span><h2>Changes across time</h2></div>
            </header>
            {!usesCanonicalSource ? <div className="dag-analysis-panel__state is-error" role="alert">Time Compare requires canonical server lineage.</div> : null}
            {usesCanonicalSource && !selectedArtifactVersion ? <div className="dag-analysis-panel__state" role="status">Select one ArtifactVersion; other record kinds cannot seed a temporal comparison.</div> : null}
            {usesCanonicalSource && selectedArtifactVersion && !timeCompareTemporalCut ? <div className="dag-analysis-panel__state" role="status">Waiting for the active exact recorded snapshot before comparing.</div> : null}
            {usesCanonicalSource && selectedArtifactVersion && timeCompareTemporalCut && timeCompareCutError ? <div className="dag-analysis-panel__state is-error" role="alert">{timeCompareCutError}</div> : null}
            {timeCompareReady ? <div className="dag-analysis-panel__state" role="status">The ordered window is ready for evidence-closed comparison.</div> : null}
          </aside>
        ) : !proofTraceRequest && !recordBrowserOpen && viewMode === "summary" && graph && summaryProjection && selectedSummaryGroupId ? (
          <SummaryGroupPanel
            sourceGraph={summaryProjection.analysisGraph}
            projection={summaryProjection}
            groupId={selectedSummaryGroupId}
            onClose={() => setSelectedSummaryGroupId(null)}
            onSelect={selectNode}
            onFocus={(nodeId) => {
              selectNode(nodeId);
              setSelectedSummaryGroupId(null);
              setViewMode("focus");
            }}
          />
        ) : !proofTraceRequest && !recordBrowserOpen && graph && selectedIndex != null && analysisPanelVisible && viewMode !== "impact" && viewMode !== "summary" ? (
          <DagDetailPanel
            graph={graph}
            nodeIndex={selectedIndex}
            outsideFilters={selectedOutsideFilters}
            onClose={() => selectNode(null)}
            onSelect={selectNode}
            onActivate={activateNode}
            onOpenProof={selectedProofContext ? () => setOpenedProofContext(selectedProofContext) : undefined}
            humanResult={displayedHumanProvenance}
          />
        ) : null}
      </div>
    </main>
  );
}
